import { useState, useEffect, useRef, useCallback } from "react";
import WorkflowsTab from "./Workflows";
import StarRules from "./StarRules";
import useChatMessages from "./useChatMessages";
import CustomerDetails from "./CustomerDetails";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001/api";

// ─── Render template body with filled-in parameters ────────
function renderTemplate(template, params) {
  const bodyComp = (template.components || []).find(c => c.type === "BODY");
  if (!bodyComp?.text) return template.name;
  return bodyComp.text.replace(/{{\d+}}/g, (match) => {
    const num = match.replace(/[{}]/g, "");
    return params[num] || match;
  });
}

// ─── Auth helpers ───────────────────────────────────────────
function getToken() { return localStorage.getItem("wa_token"); }
function setToken(t) { localStorage.setItem("wa_token", t); }
function clearToken() { localStorage.removeItem("wa_token"); }

function getUser() {
  try { return JSON.parse(localStorage.getItem("wa_user") || "null"); }
  catch { return null; }
}
function setUser(u) { localStorage.setItem("wa_user", JSON.stringify(u)); }
function clearUser() { localStorage.removeItem("wa_user"); }

// ─── Permission check ────────────────────────────────────────
function can(user, perm) {
  if (!user) return false;
  if (user.role === "admin") return true;
  if ((user.permissions || []).includes("all")) return true;
  return (user.permissions || []).includes(perm);
}

// ─── API fetch wrapper ──────────────────────────────────────
async function apiFetch(path, opts = {}) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts.headers,
    },
  });
  if (res.status === 401) { clearToken(); window.location.reload(); }
  const contentType = (res.headers.get("content-type") || "").toLowerCase();
  if (!contentType.includes("application/json") && !contentType.includes("+json")) {
    return { error: res.status === 404
      ? "This feature is unavailable on the server. Please contact your administrator to update the backend."
      : "The server returned an unexpected response. Please try again shortly." };
  }
  try { return await res.json(); }
  catch { return { error: "The server returned an invalid response. Please try again shortly." }; }
}

// ─── Message status tick component ──────────────────────────
function MessageTick({ status }) {
  if (!status || status === "sent") {
    // Single grey tick
    return (
      <svg width="16" height="11" viewBox="0 0 16 11" fill="none" style={{ display:"inline-block", verticalAlign:"middle" }}>
        <path d="M1 5.5L5.5 10L15 1" stroke="#8696a0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (status === "delivered") {
    // Double grey tick
    return (
      <svg width="20" height="11" viewBox="0 0 20 11" fill="none" style={{ display:"inline-block", verticalAlign:"middle" }}>
        <path d="M1 5.5L5.5 10L15 1" stroke="#8696a0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 5.5L9.5 10L19 1" stroke="#8696a0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  // read — double blue tick
  return (
    <svg width="20" height="11" viewBox="0 0 20 11" fill="none" style={{ display:"inline-block", verticalAlign:"middle" }}>
      <path d="M1 5.5L5.5 10L15 1" stroke="#53bdeb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 5.5L9.5 10L19 1" stroke="#53bdeb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── Icons (inline SVG) ─────────────────────────────────────
const Icons = {
  Send: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
  ),
  Template: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
  ),
  Chat: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  ),
  Logout: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
  ),
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
  ),
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ),
  WhatsApp: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  ),
  HubSpot: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4m10-10h-4M6 12H2m15.07-5.07l-2.83 2.83M9.76 14.24l-2.83 2.83m11.14 0l-2.83-2.83M9.76 9.76L6.93 6.93"/></svg>
  ),
  Profile: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ),
  Edit: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
  ),
  Lock: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
  ),
  WAProfile: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/>
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
      <line x1="12" y1="12" x2="12" y2="16"/>
      <line x1="10" y1="14" x2="14" y2="14"/>
    </svg>
  ),
  Phone: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.34 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.63a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      <line x1="18" y1="2" x2="22" y2="2"/>
      <line x1="22" y1="2" x2="22" y2="6"/>
      <line x1="15" y1="9" x2="22" y2="2"/>
    </svg>
  ),
  ApiKey: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  ),
  Docs: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  Copy: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  Agents: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Shield: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  Trash: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  ),
  Key: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  ),
  Campaign: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
  ),
  Workflow: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="2"/><circle cx="5" cy="19" r="2"/><circle cx="19" cy="19" r="2"/>
      <line x1="12" y1="7" x2="5" y2="17"/><line x1="12" y1="7" x2="19" y2="17"/>
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
    </svg>
  ),
  Chevron: ({ open }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
};

// ═══════════════════════════════════════════════════════════════
// PROFILE PANEL
// ═══════════════════════════════════════════════════════════════
function ProfilePanel({ onClose }) {
  const [profile, setProfile]       = useState(null);
  const [editName, setEditName]     = useState("");
  const [editEmail, setEditEmail]   = useState("");
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoMsg, setInfoMsg]       = useState("");

  const [showPwd, setShowPwd]       = useState(false);
  const [curPwd, setCurPwd]         = useState("");
  const [newPwd, setNewPwd]         = useState("");
  const [confPwd, setConfPwd]       = useState("");
  const [savingPwd, setSavingPwd]   = useState(false);
  const [pwdMsg, setPwdMsg]         = useState("");

  useEffect(() => {
    apiFetch("/profile").then(data => {
      setProfile(data);
      setEditName(data.displayName || "");
      setEditEmail(data.email || "");
    });
  }, []);

  const initials = (profile?.displayName || profile?.username || "?")
    .split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  const handleSaveInfo = async () => {
    setSavingInfo(true); setInfoMsg("");
    const data = await apiFetch("/profile", {
      method: "PUT",
      body: JSON.stringify({ displayName: editName, email: editEmail }),
    });
    setSavingInfo(false);
    if (data.success) {
      setProfile(p => ({ ...p, ...data.profile }));
      setInfoMsg("✓ Saved successfully");
    } else {
      setInfoMsg(data.error || "Failed to save");
    }
  };

  const handleChangePwd = async () => {
    if (newPwd !== confPwd) { setPwdMsg("Passwords do not match"); return; }
    setSavingPwd(true); setPwdMsg("");
    const data = await apiFetch("/profile/password", {
      method: "PUT",
      body: JSON.stringify({ currentPassword: curPwd, newPassword: newPwd }),
    });
    setSavingPwd(false);
    if (data.success) {
      setPwdMsg("✓ Password changed");
      setCurPwd(""); setNewPwd(""); setConfPwd("");
    } else {
      setPwdMsg(data.error || "Failed to change password");
    }
  };

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="profile-header">
          <h2>Profile</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        {!profile ? (
          <div className="template-loading">Loading...</div>
        ) : (
          <div className="profile-body">

            {/* Avatar */}
            <div className="profile-avatar-wrap">
              <div className="profile-avatar" style={{ background: profile.avatarColor }}>
                {initials}
              </div>
              <div>
                <p className="profile-display-name">{profile.displayName}</p>
                <p className="profile-role-badge">{profile.role}</p>
              </div>
            </div>

            {/* Info section */}
            <div className="profile-section">
              <h3 className="profile-section-title"><Icons.Edit /> Personal Info</h3>
              <div className="input-group">
                <label>Display Name</label>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  placeholder="Your full name" />
              </div>
              <div className="input-group">
                <label>Email</label>
                <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)}
                  placeholder="your@email.com" />
              </div>
              <div className="input-group">
                <label>Username</label>
                <input value={profile.username} disabled style={{ opacity: 0.5 }} />
              </div>
              {infoMsg && (
                <p className={`profile-msg ${infoMsg.startsWith("✓") ? "ok" : "err"}`}>{infoMsg}</p>
              )}
              <button className="btn-primary" onClick={handleSaveInfo} disabled={savingInfo}>
                {savingInfo ? "Saving..." : "Save Changes"}
              </button>
            </div>

            {/* Password section */}
            <div className="profile-section">
              <button className="profile-section-toggle" onClick={() => { setShowPwd(v => !v); setPwdMsg(""); }}>
                <Icons.Lock /> Change Password
                <span className="profile-chevron">{showPwd ? "▲" : "▼"}</span>
              </button>
              {showPwd && (
                <div className="profile-pwd-form">
                  <div className="input-group sm">
                    <label>Current Password</label>
                    <input type="password" value={curPwd} onChange={e => setCurPwd(e.target.value)}
                      placeholder="Enter current password" />
                  </div>
                  <div className="input-group sm">
                    <label>New Password</label>
                    <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                      placeholder="Min 6 characters" />
                  </div>
                  <div className="input-group sm">
                    <label>Confirm New Password</label>
                    <input type="password" value={confPwd} onChange={e => setConfPwd(e.target.value)}
                      placeholder="Repeat new password" />
                  </div>
                  {pwdMsg && (
                    <p className={`profile-msg ${pwdMsg.startsWith("✓") ? "ok" : "err"}`}>{pwdMsg}</p>
                  )}
                  <button className="btn-primary" onClick={handleChangePwd} disabled={savingPwd || !curPwd || !newPwd}>
                    {savingPwd ? "Updating..." : "Update Password"}
                  </button>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ═══════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [loginType, setLoginType] = useState("admin"); // "admin" | "agent"
  const [username, setUsername]   = useState("");
  const [password, setPassword]   = useState("");
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);

  const handleTabSwitch = (type) => {
    setLoginType(type);
    setUsername("");
    setPassword("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      if (data.token) {
        // Verify the account type matches the selected tab
        if (loginType === "admin" && data.user?.role !== "admin") {
          setError("This account is not an admin account.");
          setLoading(false);
          return;
        }
        if (loginType === "agent" && data.user?.role === "admin") {
          setError("Use Admin Login for admin accounts.");
          setLoading(false);
          return;
        }
        setToken(data.token);
        setUser(data.user);
        onLogin(data.user);
      } else {
        setError(data.error || "Login failed");
      }
    } catch { setError("Connection failed — is the server running?"); }
    setLoading(false);
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img
              src="https://myforexeye.com/hs-fs/hubfs/logo%20new.png?width=300&height=89&name=logo%20new.png"
              alt="Myforexeye"
              style={{ height: "48px", objectFit: "contain" }}
            />
          </div>
          <p className="login-subtitle" style={{ marginTop: 14 }}>WhatsApp Business Dashboard</p>
        </div>

        <div className="login-tabs">
          <button
            className={`login-tab ${loginType === "admin" ? "active" : ""}`}
            onClick={() => handleTabSwitch("admin")}
          >
            <Icons.Shield /> Admin
          </button>
          <button
            className={`login-tab ${loginType === "agent" ? "active" : ""}`}
            onClick={() => handleTabSwitch("agent")}
          >
            <Icons.User /> Agent
          </button>
        </div>

        <div className={`login-type-banner ${loginType}`}>
          {loginType === "admin"
            ? "Sign in as Administrator — full access to all features"
            : "Sign in as Agent — access based on assigned permissions"}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label>Username</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)}
              placeholder={loginType === "admin" ? "Admin username" : "Agent username"} autoFocus />
          </div>
          <div className="input-group">
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Enter password" />
          </div>
          {error && <div className="error-msg">{error}</div>}
          <button type="submit" className="btn-primary btn-full" disabled={loading}>
            {loading ? "Signing in..." : `Sign In as ${loginType === "admin" ? "Admin" : "Agent"}`}
          </button>
        </form>
        <p className="login-hint">Admin default: admin / admin123</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONTACT LIST SIDEBAR
// ═══════════════════════════════════════════════════════════════
function StarButton({ contact, onToggleStar, pending }) {
  const label = contact.starred ? "Unstar lead" : "Star lead";
  return <button className={`star-button ${contact.starred ? "is-starred" : ""}`} title={label}
    aria-label={`${label}: ${contact.name || contact.phone}`} aria-pressed={contact.starred === true} disabled={pending}
    onClick={e => { e.stopPropagation(); onToggleStar(contact); }}>{contact.starred ? "★" : "☆"}</button>;
}

function ContactList({ contacts, activeContact, onSelect, onNewChat, onToggleStar, pendingStars = {}, starError, canManageStarRules }) {
  const [search, setSearch] = useState("");
  const [searchMode, setSearchMode] = useState("contacts");
  const [incomingResults, setIncomingResults] = useState({ query: "", matches: [], error: "" });
  const [searchLoading, setSearchLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [chatFilter, setChatFilter] = useState("all");
  const [showStarRules, setShowStarRules] = useState(false);
  const [visibleCount, setVisibleCount] = useState(100);
  const query = search.trim();
  const incomingSearch = searchMode === "incoming" && query.length > 0;
  useEffect(() => { setVisibleCount(100); }, [query, searchMode, chatFilter]);

  useEffect(() => {
    if (!incomingSearch) { setSearchLoading(false); return; }
    const controller = new AbortController();
    let cancelled = false;
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetch(`/contacts/search?incoming=${encodeURIComponent(query)}`, { signal: controller.signal });
        if (cancelled) return;
        if (!Array.isArray(data.matches)) throw new Error(data.error || "Unable to search messages.");
        setIncomingResults({ query, matches: data.matches, error: "" });
      } catch (err) {
        if (!cancelled) setIncomingResults({ query, matches: [], error: err.message || "Unable to search messages." });
      } finally { if (!cancelled) setSearchLoading(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [incomingSearch, query, contacts]);

  const waitingForSearch = incomingSearch && (searchLoading || incomingResults.query !== query);
  const searchError = incomingSearch && incomingResults.query === query ? incomingResults.error : "";
  const incomingMatches = new Map(incomingResults.matches.map(match => [match.phone, match.text]));

  const filtered = contacts
    .filter(c => {
      if (chatFilter === "unread")  return (c.unread || 0) > 0;
      if (chatFilter === "replied") return c.hasOutgoing === true;
      if (chatFilter === "starred") return c.starred === true;
      return true;
    })
    .filter(c => incomingSearch
      ? !waitingForSearch && !searchError && incomingMatches.has(c.phone)
      : searchMode === "incoming" || c.name?.toLowerCase().includes(query.toLowerCase()) || c.phone?.includes(query));

  const handleNewChat = () => {
    if (newNumber.trim()) {
      onNewChat(newNumber.trim());
      setNewNumber("");
      setShowNew(false);
    }
  };

  return (
    <div className="contact-list">
      {showStarRules && <StarRules apiFetch={apiFetch} onClose={() => setShowStarRules(false)} />}
      <label className="chat-search-mode">
        Search by
        <select value={searchMode} onChange={e => setSearchMode(e.target.value)}>
          <option value="contacts">Name or phone</option>
          <option value="incoming">Incoming message text</option>
        </select>
      </label>
      <div className="contact-search">
        <Icons.Search />
        <input
          placeholder={searchMode === "incoming" ? "Search incoming messages…" : "Search contacts..."}
          aria-label={searchMode === "incoming" ? "Search incoming messages" : "Search contacts"}
          maxLength={200}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button className="btn-icon" aria-label="Clear search" onClick={() => setSearch("")}>✕</button>}
      </div>
      {searchMode === "incoming" && <p className="chat-search-hint">Search received messages across the full chat history.</p>}
      {waitingForSearch && <p className="chat-search-hint" role="status">Searching incoming messages…</p>}
      {searchError && !waitingForSearch && <p className="star-error" role="alert">{searchError}</p>}
      <div className="chat-filter-tabs">
        {[["all", "All"], ["unread", "Unread"], ["replied", "Replied"], ["starred", "Starred"]].map(([val, label]) => (
          <button
            key={val}
            className={`chat-filter-tab ${chatFilter === val ? "active" : ""}`}
            aria-pressed={chatFilter === val}
            onClick={() => setChatFilter(val)}
          >
            {label}
            {val === "unread" && contacts.some(c => (c.unread || 0) > 0) && (
              <span className="chat-filter-dot" />
            )}
          </button>
        ))}
      </div>
      {canManageStarRules && <button className="btn-new-chat" onClick={() => setShowStarRules(true)}>☆ Star rules</button>}
      {starError && <p className="star-error" role="alert">{starError}</p>}
      <button className="btn-new-chat" onClick={() => setShowNew(!showNew)}>
        + New Conversation
      </button>
      {showNew && (
        <div className="new-chat-form">
          <input
            placeholder="e.g. +919876543210 or 919876543210"
            value={newNumber}
            onChange={e => setNewNumber(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleNewChat()}
          />
          <button onClick={handleNewChat} className="btn-sm">Start</button>
        </div>
      )}
      <div className="contact-items">
        {filtered.slice(0, visibleCount).map(c => (
          <div
            key={c.phone}
            className={`contact-item ${activeContact?.phone === c.phone ? "active" : ""}`}
            onClick={() => onSelect(c)}
          >
            <div className="contact-avatar">
              <Icons.User />
            </div>
            <div className="contact-info">
              <span className="contact-name">
                {c.name || c.phone}
                {c.opted_out && <span className="optout-badge">Opted Out</span>}
              </span>
              <span className="contact-last" title={incomingSearch ? incomingMatches.get(c.phone) : undefined}>
                {incomingSearch ? `Received: ${incomingMatches.get(c.phone)}` : c.lastMessage || "No messages yet"}
              </span>
            </div>
            {c.unread > 0 && <span className="unread-badge">{c.unread}</span>}
            <StarButton contact={c} onToggleStar={onToggleStar} pending={pendingStars[c.phone]} />
          </div>
        ))}
        {filtered.length > visibleCount && <button className="btn-new-chat" onClick={() => setVisibleCount(n => n + 100)}>
          Show more conversations ({filtered.length - visibleCount} remaining)
        </button>}
        {filtered.length === 0 && !waitingForSearch && !searchError && (
          <div className="empty-contacts">{search ? "No matching conversations" : chatFilter === "starred" ? "No starred leads yet. Star a conversation to find it here." : chatFilter === "all" ? "No conversations yet" : `No ${chatFilter} conversations`}</div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CHAT WINDOW
// ═══════════════════════════════════════════════════════════════
function ChatWindow({ contact, messages, onSend, onSendTemplate, canSend = true, canSendTemplate = true, onBack, phones = [], fromPhoneId, onFromPhoneChange, onToggleOptOut, isAdmin = false, onToggleStar, pendingStars = {}, starError, history }) {
  const [text, setText] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [showMediaInput, setShowMediaInput] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const bottomRef = useRef(null);
  const messageListRef = useRef(null);
  const scrollSnapshot = useRef(null);
  const scrollPosition = useRef({ phone: null, lastMessage: null });

  useEffect(() => {
    const element = messageListRef.current;
    if (!element) return;
    const lastMessage = messages[messages.length - 1];
    const lastKey = lastMessage ? `${lastMessage.wa_id || ""}:${lastMessage.timestamp}:${lastMessage.text}` : null;
    const switched = scrollPosition.current.phone !== contact?.phone;
    if (!switched && scrollSnapshot.current && history?.start < scrollSnapshot.current.start) {
      element.scrollTop = scrollSnapshot.current.top + element.scrollHeight - scrollSnapshot.current.height;
      scrollSnapshot.current = null;
    } else if (switched || (lastKey && !scrollPosition.current.lastMessage)) {
      element.scrollTop = element.scrollHeight;
    } else if (lastKey !== scrollPosition.current.lastMessage && element.scrollHeight - element.scrollTop - element.clientHeight < 250) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    if (switched) scrollSnapshot.current = null;
    scrollPosition.current = { phone: contact?.phone, lastMessage: lastKey };
  }, [messages, contact?.phone, history?.start]);

  const handleSend = () => {
    if (text.trim() || mediaUrl.trim()) {
      onSend(text.trim(), mediaUrl.trim() || null);
      setText("");
      setMediaUrl("");
      setShowMediaInput(false);
    }
  };

  if (!contact) {
    return (
      <div className="chat-window chat-empty">
        <div className="empty-state">
          <Icons.WhatsApp />
          <h2>WhatsApp Business Dashboard</h2>
          <p>Select a conversation or start a new one</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-info">
          {onBack && (
            <button className="btn-icon mobile-back-btn" onClick={onBack} title="Back">
              <Icons.ArrowLeft />
            </button>
          )}
          <div className="contact-avatar sm">{(contact.name || contact.phone || "?")[0].toUpperCase()}</div>
          <div>
            <h3>{contact.name || contact.phone}</h3>
            <span className="phone-number">+{contact.phone}</span>
          </div>
        </div>
        {contact.opted_out && isAdmin && (
          <button
            className="btn-sm optout-reinstate-btn"
            onClick={() => onToggleOptOut && onToggleOptOut(contact.phone, false)}
            title="Remove opt-out flag and allow messaging again"
          >
            Re-opt In
          </button>
        )}
        {!contact.opted_out && isAdmin && (
          <button
            className="btn-sm optout-mark-btn"
            onClick={() => onToggleOptOut && onToggleOptOut(contact.phone, true)}
            title="Mark this contact as opted out"
          >
            Mark Opt-Out
          </button>
        )}
        <StarButton contact={contact} onToggleStar={onToggleStar} pending={pendingStars[contact.phone]} />
      </div>

      {starError && <p className="star-error" role="alert">{starError}</p>}
      {contact.opted_out && (
        <div className="optout-banner">
          This contact has opted out of messages. Sending is disabled.
          {contact.optedOutAt && (
            <span className="optout-banner-time"> (since {new Date(contact.optedOutAt).toLocaleDateString()})</span>
          )}
        </div>
      )}

      <div className="chat-messages" ref={messageListRef}>
        {history?.loading && <p role="status">Loading messages…</p>}
        {history?.error && <p className="star-error" role="alert">{history.error} <button className="btn-sm" onClick={history.refresh}>Retry</button></p>}
        {history?.hasMore && <button className="btn-sm" disabled={history.loadingOlder} onClick={() => {
          const element = messageListRef.current;
          scrollSnapshot.current = { height: element.scrollHeight, top: element.scrollTop, start: history.start };
          history.loadOlder();
        }}>{history.loadingOlder ? "Loading…" : "Load older messages"}</button>}
        {messages.map((m, i) => (
          <div key={i} className={`message ${m.direction}`}>
            <div className="message-bubble">
              <p>{m.text}</p>
              <span className="message-time">
                {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {m.hubspot_synced && <span className="hs-badge" title="Synced to HubSpot">● HS</span>}
                {m.direction === "outgoing" && (
                  <span className="msg-tick" title={m.status || "sent"}>
                    <MessageTick status={m.status} />
                  </span>
                )}
              </span>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {canSendTemplate && showTemplates && (
        <TemplateModal
          onSend={(tpl, params) => { onSendTemplate(tpl, params); setShowTemplates(false); }}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Phone selector — shown only when more than one number is registered */}
      {phones.length > 1 && onFromPhoneChange && (
        <div className="chat-phone-selector">
          <span className="chat-phone-selector-label">Send from:</span>
          <select
            value={fromPhoneId || ""}
            onChange={e => onFromPhoneChange(e.target.value)}
            className="chat-phone-select"
          >
            {phones.map(p => (
              <option key={p.phone_number_id} value={p.phone_number_id}>
                {p.display_phone_number || p.phone_number_id}
                {p.verified_name ? ` — ${p.verified_name}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Media URL input */}
      {canSend && showMediaInput && (
        <div className="chat-media-bar">
          <input
            className="chat-media-input"
            placeholder="Paste image or video URL (e.g. https://example.com/image.jpg)"
            value={mediaUrl}
            onChange={e => setMediaUrl(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSend()}
          />
          <button className="btn-icon" onClick={() => { setMediaUrl(""); setShowMediaInput(false); }} title="Remove media">✕</button>
        </div>
      )}

      <div className="chat-input">
        {canSendTemplate && !contact.opted_out && (
          <button
            className={`btn-template-toggle ${showTemplates ? "active" : ""}`}
            onClick={() => setShowTemplates(!showTemplates)}
            title="Send Template"
          >
            <Icons.Template />
            <span>Template</span>
          </button>
        )}
        {canSend && !contact.opted_out && (
          <button
            className={`btn-template-toggle ${showMediaInput ? "active" : ""}`}
            onClick={() => setShowMediaInput(v => !v)}
            title="Attach Image / Video URL"
            style={{ fontSize: 18 }}
          >
            📎
          </button>
        )}
        <input
          placeholder={contact.opted_out ? "Contact has opted out — messaging disabled" : canSend ? (mediaUrl ? "Add a caption (optional)…" : "Type a message...") : "View only — no send permission"}
          value={text}
          onChange={e => (canSend && !contact.opted_out) && setText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && canSend && !contact.opted_out && handleSend()}
          disabled={!canSend || contact.opted_out}
          style={(!canSend || contact.opted_out) ? { opacity: 0.6, cursor: "not-allowed" } : {}}
        />
        <button className="btn-send" onClick={handleSend} disabled={(!text.trim() && !mediaUrl.trim()) || !canSend || contact.opted_out}>
          <Icons.Send />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMPLATE MODAL  (centered popup)
// ═══════════════════════════════════════════════════════════════
function TemplateModal({ onSend, onClose }) {
  const [templates,    setTemplates]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(null);
  const [params,       setParams]       = useState({});
  const [search,       setSearch]       = useState("");
  useEffect(() => {
    apiFetch("/templates").then(data => {
      setTemplates(data.templates || []);
      setLoading(false);
    });
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.language?.toLowerCase().includes(search.toLowerCase())
  );

  const getVariables = (tpl) => {
    const vars = [];
    (tpl.components || []).forEach(comp => {
      const matches = (comp.text || "").match(/{{\d+}}/g);
      if (matches) matches.forEach(m => {
        const num = m.replace(/[{}]/g, "");
        if (!vars.includes(num)) vars.push(num);
      });
    });
    return vars;
  };

  // Live preview: replace {{n}} with filled param values
  const getPreviewText = (tpl, p) => {
    const bodyComp = tpl.components?.find(c => c.type === "BODY");
    if (!bodyComp?.text) return "";
    return bodyComp.text.replace(/{{\d+}}/g, match => {
      const num = match.replace(/[{}]/g, "");
      return p[num] || match;
    });
  };

  const handleSend = () => {
    if (selected) onSend(selected, params);
  };

  const vars = selected ? getVariables(selected) : [];
  const headerComp  = selected?.components?.find(c => c.type === "HEADER");
  const bodyComp    = selected?.components?.find(c => c.type === "BODY");
  const footerComp  = selected?.components?.find(c => c.type === "FOOTER");
  const buttonsComp = selected?.components?.find(c => c.type === "BUTTONS");

  return (
    <div className="tpl-modal-overlay" onClick={onClose}>
      <div className="tpl-send-modal" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="tpl-send-modal-header">
          <div>
            <h3>Send Template</h3>
            <p className="tpl-send-modal-sub">Pick a template, fill in the variables, then send.</p>
          </div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        {/* ── Body: two columns ── */}
        <div className="tpl-send-modal-body">

          {/* LEFT: search + list */}
          <div className="tpl-send-left">
            <div className="tpl-send-search">
              <Icons.Search />
              <input
                autoFocus
                placeholder="Search templates…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="template-loading">Loading…</div>
            ) : (
              <div className="tpl-send-list">
                {filtered.map((t, i) => (
                  <div
                    key={i}
                    className={`tpl-send-item ${selected?.name === t.name && selected?.language === t.language ? "selected" : ""}`}
                    onClick={() => { setSelected(t); setParams({}); }}
                  >
                    <div className="tpl-send-item-top">
                      <span className="tpl-send-item-name">{t.name}</span>
                      <span className={`template-status ${t.status}`}>{t.status === "APPROVED" ? "Active" : t.status}</span>
                    </div>
                    <span className="tpl-send-item-lang">{t.language}</span>
                    {t.components?.find(c => c.type === "BODY") && (
                      <p className="tpl-send-item-body">
                        {t.components.find(c => c.type === "BODY").text?.slice(0, 80)}
                        {t.components.find(c => c.type === "BODY").text?.length > 80 ? "…" : ""}
                      </p>
                    )}
                  </div>
                ))}
                {filtered.length === 0 && (
                  <div className="template-loading">No templates match your search.</div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: variables + preview */}
          <div className="tpl-send-right">
            {!selected ? (
              <div className="tpl-send-placeholder">
                <Icons.Template />
                <p>Select a template on the left to fill in variables and preview it.</p>
              </div>
            ) : (
              <>
                {/* Variable inputs */}
                {vars.length > 0 && (
                  <div className="tpl-send-vars">
                    <p className="tpl-send-section-label">Variables</p>
                    {vars.map(v => (
                      <div key={v} className="input-group sm">
                        <label>Variable {`{{${v}}}`}</label>
                        <input
                          placeholder={`Value for {{${v}}}`}
                          value={params[v] || ""}
                          onChange={e => setParams({ ...params, [v]: e.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Preview bubble */}
                <p className="tpl-send-section-label" style={{ marginTop: vars.length ? 16 : 0 }}>Preview</p>
                <div className="tpl-send-preview">
                  <div className="tpl-bubble">
                    {headerComp?.format === "TEXT" && headerComp.text && (
                      <p className="tpl-header-text">{headerComp.text}</p>
                    )}
                    {headerComp?.format && headerComp.format !== "TEXT" && (
                      <div className="tpl-media-placeholder">{headerComp.format}</div>
                    )}
                    {bodyComp?.text && (
                      <p className="tpl-body-text">{getPreviewText(selected, params)}</p>
                    )}
                    {footerComp?.text && (
                      <p className="tpl-footer-text">{footerComp.text}</p>
                    )}
                    <span className="ct-preview-time">
                      {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  {buttonsComp?.buttons?.map((btn, i) => (
                    <div key={i} className="ct-preview-btn">
                      {btn.type === "URL" ? "🔗 " : btn.type === "PHONE_NUMBER" ? "📞 " : "↩ "}
                      {btn.text}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="tpl-send-modal-footer">
          <button className="btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={!selected}
            style={{ padding: "9px 28px" }}
          >
            <Icons.Send /> Send Template
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PHONE NUMBERS VIEW  (Meta Embedded Signup)
// ═══════════════════════════════════════════════════════════════
const SIGNUP_URL =
  "https://business.facebook.com/messaging/whatsapp/onboard/" +
  "?app_id=1247449740124271&config_id=1545053880549154" +
  "&extras=%7B%22sessionInfoVersion%22%3A%223%22%2C%22version%22%3A%22v4%22%7D";

function PhoneNumbersView() {
  const [phones,      setPhones]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [signing,     setSigning]     = useState(false);
  const [msg,         setMsg]         = useState({ text: "", ok: true });
  // tokenModal: { phone_number_id, waba_id, display_phone_number } | null
  const [tokenModal,  setTokenModal]  = useState(null);
  const [tokenInput,  setTokenInput]  = useState("");
  const [savingToken, setSavingToken] = useState(false);

  const fetchPhones = () => {
    setLoading(true);
    apiFetch("/phones").then(d => {
      setPhones(d.phones || []);
      setLoading(false);
      if (d.error) setMsg({ text: d.error, ok: false });
    });
  };

  useEffect(() => { fetchPhones(); }, []);

  const openSignup = () => {
    const w = 620, h = 720;
    const left = Math.max(0, (window.screen.width  - w) / 2);
    const top  = Math.max(0, (window.screen.height - h) / 2);
    const popup = window.open(
      SIGNUP_URL,
      "wa_embedded_signup",
      `width=${w},height=${h},left=${left},top=${top},toolbar=0,menubar=0,scrollbars=1,resizable=1`
    );
    if (!popup) {
      setMsg({ text: "Popup was blocked. Please allow popups for this site and try again.", ok: false });
      return;
    }
    setSigning(true);
    setMsg({ text: "", ok: true });

    const handler = (event) => {
      if (!event.origin.includes("facebook.com") && !event.origin.includes("meta.com")) return;
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "WA_EMBEDDED_SIGNUP") return;

        window.removeEventListener("message", handler);
        setSigning(false);
        popup?.close();

        if (data.event === "FINISH") {
          const { phone_number_id, waba_id } = data.data || {};
          if (!phone_number_id || !waba_id) {
            setMsg({ text: "Signup completed but no phone data received. Please try again.", ok: false });
            return;
          }
          // Register the phone first, then prompt for the access token
          registerPhone(phone_number_id, waba_id, null, () => {
            setTokenModal({ phone_number_id, waba_id, display_phone_number: waba_id });
            setTokenInput("");
          });
        } else if (data.event === "CANCEL") {
          setMsg({ text: "Signup cancelled. No phone number was added.", ok: false });
        } else if (data.event === "ERROR") {
          setMsg({ text: `Signup error: ${data.data?.error_message || "Unknown error"}`, ok: false });
        }
      } catch (_) {}
    };

    window.addEventListener("message", handler);

    const pollClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollClosed);
        window.removeEventListener("message", handler);
        setSigning(false);
      }
    }, 800);
  };

  const registerPhone = async (phone_number_id, waba_id, access_token, onSuccess) => {
    setMsg({ text: "Verifying phone number with Meta…", ok: true });
    const data = await apiFetch("/phones/register", {
      method: "POST",
      body: JSON.stringify({ phone_number_id, waba_id, ...(access_token ? { access_token } : {}) }),
    });
    if (data.success) {
      fetchPhones();
      setMsg({ text: data.already ? "Already connected." : "✓ Phone number added!", ok: true });
      onSuccess?.();
    } else {
      setMsg({ text: data.details?.error?.message || data.error || "Failed to register phone.", ok: false });
    }
  };

  const handleSaveToken = async () => {
    if (!tokenModal || !tokenInput.trim()) return;
    setSavingToken(true);
    // If the phone is registered in our DB, use the token endpoint
    const inStore = phones.find(p => p.phone_number_id === tokenModal.phone_number_id);
    if (inStore) {
      const data = await apiFetch(`/phones/${tokenModal.phone_number_id}/token`, {
        method: "PUT",
        body: JSON.stringify({ access_token: tokenInput.trim() }),
      });
      setSavingToken(false);
      if (data.success) {
        setMsg({ text: "✓ Access token saved — this WABA is now fully connected.", ok: true });
        setTokenModal(null);
        fetchPhones();
      } else {
        setMsg({ text: data.error || "Failed to save token.", ok: false });
      }
    } else {
      // Phone not yet in store — register with the token
      await registerPhone(tokenModal.phone_number_id, tokenModal.waba_id, tokenInput.trim());
      setSavingToken(false);
      setTokenModal(null);
    }
  };

  const qualityColor = (q) =>
    q === "GREEN"  ? "var(--green-primary)"  :
    q === "YELLOW" ? "var(--orange)"          :
    q === "RED"    ? "var(--danger)"          : "var(--text-muted)";

  const qualityLabel = (q) =>
    q === "GREEN" ? "High" : q === "YELLOW" ? "Medium" : q === "RED" ? "Low" : q || "—";

  const statusBadge = (s) => {
    const map = {
      CONNECTED:    { label: "Connected",    cls: "APPROVED" },
      DISCONNECTED: { label: "Disconnected", cls: "REJECTED" },
      PENDING:      { label: "Pending",      cls: "PENDING"  },
    };
    return map[s] || { label: s || "Unknown", cls: "PENDING" };
  };

  return (
    <div className="pn-root">
      <div className="pn-header">
        <div>
          <h2>Phone Numbers</h2>
          <p className="wap-subtitle">All WhatsApp Business numbers — each can have its own access token</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn-sm" onClick={fetchPhones}><Icons.Refresh /> Refresh</button>
          <button className="btn-primary pn-add-btn" onClick={openSignup} disabled={signing}>
            {signing
              ? <><span className="pn-spinner" /> Waiting for Meta…</>
              : <><span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Number</>
            }
          </button>
        </div>
      </div>

      {msg.text && (
        <div className={`wap-alert ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>
      )}

      <div className="pn-body">
        {loading ? (
          <div className="template-loading">Loading phone numbers from Meta…</div>
        ) : phones.length === 0 ? (
          <div className="template-loading">No phone numbers found. Add one using the button above.</div>
        ) : (
          <div className="pn-table-wrap">
            <table className="tpl-table">
              <thead>
                <tr>
                  <th>Phone Number</th>
                  <th>Display Name</th>
                  <th>Status</th>
                  <th>Quality</th>
                  <th>API Access</th>
                  <th>Phone Number ID</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {phones.map((p) => {
                  const badge = statusBadge(p.status);
                  return (
                    <tr key={p.id} className="tpl-row" style={{ cursor: "default" }}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div className="pn-row-icon"><Icons.WhatsApp /></div>
                          <span style={{ fontWeight: 600, fontSize: 15 }}>{p.display_phone_number || "—"}</span>
                        </div>
                      </td>
                      <td style={{ color: "var(--text-secondary)" }}>{p.verified_name || "—"}</td>
                      <td><span className={`tpl-status-badge ${badge.cls}`}>{badge.label}</span></td>
                      <td>
                        <span className="pn-quality-cell" style={{ color: qualityColor(p.quality_rating) }}>
                          ● {qualityLabel(p.quality_rating)}
                        </span>
                      </td>
                      <td>
                        {p.access_token_set
                          ? <span style={{ color: "var(--green-primary)", fontSize: 12, fontWeight: 600 }}>✓ Token set</span>
                          : <span style={{ color: "var(--orange)", fontSize: 12 }}>⚠ Using default</span>
                        }
                      </td>
                      <td><code className="pn-id-code">{p.id}</code></td>
                      <td>
                        <button className="btn-sm" style={{ fontSize: 11 }}
                          onClick={() => { setTokenModal({ phone_number_id: p.id, waba_id: p.waba_id, display_phone_number: p.display_phone_number }); setTokenInput(""); }}>
                          {p.access_token_set ? "Update Token" : "Set Token"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="pn-howto" style={{ marginTop: 28 }}>
          <h4>Multi-number setup — no manual token changes needed</h4>
          <ol>
            <li>Click <strong>+ Add Number</strong> and complete Meta's signup.</li>
            <li>After signup, paste the <strong>System User access token</strong> for that WhatsApp Business Account.</li>
            <li>Each phone number stores its own token — the system uses the right one automatically.</li>
            <li>When sending a message, choose which number to send from using the selector in the chat window.</li>
          </ol>
          <p className="pn-howto-note">
            To get a System User token: go to <strong>Meta Business Suite → Settings → System Users</strong>, select your system user, click <strong>Generate New Token</strong>, and choose the relevant WhatsApp Business Account with <code>whatsapp_business_messaging</code> + <code>whatsapp_business_management</code> permissions.
          </p>
        </div>
      </div>

      {/* Token Modal */}
      {tokenModal && (
        <div className="modal-overlay" onClick={() => setTokenModal(null)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Set Access Token</h3>
              <button className="btn-icon" onClick={() => setTokenModal(null)}>✕</button>
            </div>
            <div style={{ padding: "16px 20px 8px" }}>
              <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 14 }}>
                Paste the <strong>System User access token</strong> for WhatsApp Business Account (WABA) <code style={{ fontSize: 11 }}>{tokenModal.waba_id}</code>.
                This allows the dashboard to send messages from <strong>{tokenModal.display_phone_number || tokenModal.phone_number_id}</strong> without changing the global token.
              </p>
              <div className="input-group">
                <label>System User Access Token</label>
                <input
                  type="password"
                  placeholder="EAAxxxxx…"
                  value={tokenInput}
                  onChange={e => setTokenInput(e.target.value)}
                  autoFocus
                />
              </div>
              <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                Meta Business Suite → Settings → System Users → Generate New Token → select this WABA → permissions: <code>whatsapp_business_messaging</code>, <code>whatsapp_business_management</code>
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleSaveToken} disabled={savingToken || !tokenInput.trim()}>
                {savingToken ? "Saving…" : "Save Token"}
              </button>
              <button className="btn-sm" onClick={() => setTokenModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// WHATSAPP BUSINESS PROFILE VIEW
// ═══════════════════════════════════════════════════════════════
const WA_VERTICALS = [
  { value: "UNDEFINED",     label: "Select category…" },
  { value: "AUTO",          label: "Automotive" },
  { value: "BEAUTY",        label: "Beauty, Spa & Salon" },
  { value: "APPAREL",       label: "Clothing & Apparel" },
  { value: "EDU",           label: "Education" },
  { value: "ENTERTAIN",     label: "Entertainment" },
  { value: "EVENT_PLAN",    label: "Event Planning" },
  { value: "FINANCE",       label: "Finance & Banking" },
  { value: "GROCERY",       label: "Food & Grocery" },
  { value: "GOVT",          label: "Government & Politics" },
  { value: "HOTEL",         label: "Hotel & Lodging" },
  { value: "HEALTH",        label: "Health & Medical" },
  { value: "NONPROFIT",     label: "Non-profit" },
  { value: "PROF_SERVICES", label: "Professional Services" },
  { value: "RETAIL",        label: "Shopping & Retail" },
  { value: "TRAVEL",        label: "Travel & Transportation" },
  { value: "RESTAURANT",    label: "Restaurant" },
  { value: "OTHER",         label: "Other" },
];

const EMPTY_WA = { about: "", address: "", description: "", email: "", website1: "", website2: "", vertical: "UNDEFINED" };

function WAProfileView() {
  const [form, setForm]         = useState(EMPTY_WA);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [profilePic, setPic]    = useState("");
  const [msg, setMsg]           = useState({ text: "", ok: true });

  useEffect(() => {
    apiFetch("/wa-profile").then(data => {
      if (data.profile) {
        const p = data.profile;
        setForm({
          about:       p.about        || "",
          address:     p.address      || "",
          description: p.description  || "",
          email:       p.email        || "",
          website1:    p.websites?.[0] || "",
          website2:    p.websites?.[1] || "",
          vertical:    p.vertical     || "UNDEFINED",
        });
        setPic(p.profile_picture_url || "");
      }
      setLoading(false);
    });
  }, []);

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true); setMsg({ text: "", ok: true });
    const websites = [form.website1, form.website2].filter(Boolean);
    const data = await apiFetch("/wa-profile", {
      method: "PATCH",
      body: JSON.stringify({
        about:       form.about,
        address:     form.address,
        description: form.description,
        email:       form.email,
        websites,
        ...(form.vertical !== "UNDEFINED" ? { vertical: form.vertical } : {}),
      }),
    });
    setSaving(false);
    if (data.success) {
      setMsg({ text: "WhatsApp Business Profile updated successfully!", ok: true });
    } else {
      setMsg({ text: data.details?.error?.message || data.error || "Failed to update profile.", ok: false });
    }
  };

  if (loading) return (
    <div className="wap-root"><div className="template-loading">Loading WhatsApp Business Profile…</div></div>
  );

  const categoryLabel = WA_VERTICALS.find(v => v.value === form.vertical)?.label || "";

  return (
    <div className="wap-root">
      <div className="wap-header">
        <div>
          <h2>WhatsApp Business Profile</h2>
          <p className="wap-subtitle">Changes are reflected on your WhatsApp Business account</p>
        </div>
        <button className="btn-primary" style={{ padding: "9px 22px" }} onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {msg.text && (
        <div className={`wap-alert ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>
      )}

      <div className="wap-body">
        {/* ── FORM ── */}
        <div className="wap-form">

          {/* Avatar row */}
          <div className="wap-section">
            <h3 className="wap-section-title">Profile Picture</h3>
            <div className="wap-avatar-row">
              <div className="wap-avatar">
                {profilePic
                  ? <img src={profilePic} alt="profile" style={{ width:"100%", height:"100%", borderRadius:"50%", objectFit:"cover" }} />
                  : <Icons.WhatsApp />}
              </div>
              <p className="wap-avatar-hint">Profile picture is managed through the WhatsApp Business app or Meta Business Suite.</p>
            </div>
          </div>

          {/* About */}
          <div className="wap-section">
            <h3 className="wap-section-title">About</h3>
            <div className="input-group">
              <label>About <span className="ct-opt">Shown under your name in the profile</span></label>
              <input
                placeholder="e.g. Helping businesses grow with forex solutions"
                maxLength={139}
                value={form.about}
                onChange={e => f("about", e.target.value)}
              />
              <span className="ct-char" style={{ display:"block", textAlign:"right", marginTop:4 }}>{form.about.length}/139</span>
            </div>
            <div className="input-group">
              <label>Business Category</label>
              <select value={form.vertical} onChange={e => f("vertical", e.target.value)}>
                {WA_VERTICALS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="wap-section">
            <h3 className="wap-section-title">Description</h3>
            <div className="input-group">
              <label>Business Description</label>
              <textarea
                placeholder="Describe your business, services, and what makes you unique…"
                rows={4} maxLength={512}
                value={form.description}
                onChange={e => f("description", e.target.value)}
              />
              <span className="ct-char" style={{ display:"block", textAlign:"right", marginTop:4 }}>{form.description.length}/512</span>
            </div>
          </div>

          {/* Contact */}
          <div className="wap-section">
            <h3 className="wap-section-title">Contact Info</h3>
            <div className="input-group">
              <label>Business Email</label>
              <input type="email" placeholder="contact@myforexeye.com" value={form.email} onChange={e => f("email", e.target.value)} />
            </div>
            <div className="input-group">
              <label>Website 1</label>
              <input placeholder="https://myforexeye.com" value={form.website1} onChange={e => f("website1", e.target.value)} />
            </div>
            <div className="input-group">
              <label>Website 2 <span className="ct-opt">Optional</span></label>
              <input placeholder="https://blog.myforexeye.com" value={form.website2} onChange={e => f("website2", e.target.value)} />
            </div>
          </div>

          {/* Address */}
          <div className="wap-section">
            <h3 className="wap-section-title">Address</h3>
            <div className="input-group">
              <label>Business Address</label>
              <textarea
                placeholder="Floor, Building, Street, City, State, PIN"
                rows={2} maxLength={256}
                value={form.address}
                onChange={e => f("address", e.target.value)}
              />
            </div>
          </div>

        </div>

        {/* ── PREVIEW ── */}
        <div className="wap-preview">
          <h3 className="wap-preview-title">Live Preview</h3>
          <div className="wap-card">
            <div className="wap-card-avatar">
              {profilePic
                ? <img src={profilePic} alt="" style={{ width:"100%", height:"100%", borderRadius:"50%", objectFit:"cover" }} />
                : <Icons.WhatsApp />}
            </div>
            <p className="wap-card-name">Myforexeye</p>
            {form.vertical !== "UNDEFINED" && <p className="wap-card-category">{categoryLabel}</p>}
            {form.about && <p className="wap-card-about">{form.about}</p>}

            {(form.description || form.email || form.website1 || form.website2 || form.address) && (
              <div className="wap-card-divider" />
            )}
            {form.description && (
              <div className="wap-card-row"><span>📋</span><span>{form.description}</span></div>
            )}
            {form.email && (
              <div className="wap-card-row"><span>✉️</span><span>{form.email}</span></div>
            )}
            {form.website1 && (
              <div className="wap-card-row"><span>🌐</span><span>{form.website1}</span></div>
            )}
            {form.website2 && (
              <div className="wap-card-row"><span>🌐</span><span>{form.website2}</span></div>
            )}
            {form.address && (
              <div className="wap-card-row"><span>📍</span><span>{form.address}</span></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// API KEYS VIEW (Admin only)
// ═══════════════════════════════════════════════════════════════
function ApiKeysView() {
  const [keys, setKeys]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [name, setName]             = useState("");
  const [creating, setCreating]     = useState(false);
  const [newKey, setNewKey]         = useState(null);   // shown once after creation
  const [msg, setMsg]               = useState({ text: "", ok: true });
  const [copied, setCopied]         = useState(false);
  const [webhookModal, setWebhookModal] = useState(null); // { id, name, webhookUrl, webhookSecret }

  const fetch = async () => {
    setLoading(true);
    const data = await apiFetch("/apikeys");
    setKeys(data.apikeys || []);
    setLoading(false);
  };
  useEffect(() => { fetch(); }, []);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: "", ok: true }), 3500); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    const data = await apiFetch("/apikeys", { method: "POST", body: JSON.stringify({ name }) });
    setCreating(false);
    if (data.success) {
      setNewKey(data.apikey);
      setName("");
      fetch();
    } else {
      flash(data.error || "Failed to create key", false);
    }
  };

  const handleRevoke = async (id, keyName) => {
    if (!window.confirm(`Revoke API key "${keyName}"? All integrations using this key will stop working.`)) return;
    const data = await apiFetch(`/apikeys/${id}`, { method: "DELETE" });
    if (data.success) { flash("API key revoked."); fetch(); }
    else flash(data.error || "Failed to revoke", false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).then(() => flash(`✓ ${label} copied to clipboard`));
  };

  const handleSaveWebhook = async () => {
    const { id, webhookUrl, regenerateSecret } = webhookModal;
    const data = await apiFetch(`/apikeys/${id}/webhook`, {
      method: "PUT",
      body: JSON.stringify({ webhookUrl: webhookUrl.trim(), regenerateSecret }),
    });
    if (data.success) {
      flash(webhookUrl.trim() ? "Webhook configured." : "Webhook removed.");
      setWebhookModal(m => ({ ...m, webhookSecret: data.webhookSecret, webhookUrl: data.webhookUrl || "" }));
      fetch();
    } else {
      flash(data.error || "Failed to save webhook", false);
    }
  };

  const handleTestWebhook = async (id) => {
    const data = await apiFetch(`/apikeys/${id}/webhook/test`, { method: "POST" });
    if (data.success) flash(`✓ Test ping sent to ${data.sentTo}`);
    else flash(data.error || "Test delivery failed", false);
  };

  return (
    <div className="ak-root">
      <div className="am-header">
        <div>
          <h2>API Keys</h2>
          <p className="wap-subtitle">Manage API keys for programmatic access to WhatsApp messaging</p>
        </div>
      </div>

      {msg.text && <div className={`wap-alert ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>}

      {/* New key revealed once */}
      {newKey && (
        <div className="ak-newkey-banner">
          <div className="ak-newkey-header">
            <span className="ak-newkey-title">API Key Created — Copy it now, it won't be shown again</span>
            <button className="btn-icon" onClick={() => setNewKey(null)}>✕</button>
          </div>
          <div className="ak-newkey-row">
            <code className="ak-key-display">{newKey.key}</code>
            <button className="btn-sm ak-copy-btn" onClick={() => copyToClipboard(newKey.key)}>
              <Icons.Copy /> {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <p className="ak-newkey-hint">Store this in your application's environment variables as <code>MFE_API_KEY</code>.</p>
        </div>
      )}

      {/* Create form */}
      <form className="ak-create-form" onSubmit={handleCreate}>
        <h3>Create New API Key</h3>
        <div className="ak-create-row">
          <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>Key Name <span className="ct-req">*</span></label>
            <input placeholder="e.g. Production Integration, CRM Sync" value={name}
              onChange={e => setName(e.target.value)} required />
          </div>
          <button type="submit" className="btn-primary ak-create-btn" disabled={creating || !name.trim()}>
            {creating ? "Creating…" : "Generate Key"}
          </button>
        </div>
      </form>

      {/* Keys table */}
      {loading ? (
        <div className="template-loading">Loading API keys…</div>
      ) : keys.length === 0 ? (
        <div className="template-loading">No API keys yet. Create one above.</div>
      ) : (
        <div className="am-table-wrap">
          <table className="tpl-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key (masked)</th>
                <th>Created</th>
                {can(getUser(), "send_campaign") && <th>Actions</th>}
                <th>Last Used</th>
                <th>Requests</th>
                <th>Status</th>
                <th>Webhook</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} className="tpl-row" style={{ cursor: "default" }}>
                  <td><span style={{ fontWeight: 600 }}>{k.name}</span><br/><span className="am-agent-username">by {k.createdBy}</span></td>
                  <td><code className="ak-key-preview">{k.keyPreview}</code></td>
                  <td style={{ color: "var(--text-secondary)", fontSize: 12 }}>{new Date(k.createdAt).toLocaleDateString()}</td>
                  <td style={{ color: "var(--text-secondary)", fontSize: 12 }}>{k.lastUsed ? new Date(k.lastUsed).toLocaleString() : "Never"}</td>
                  <td style={{ textAlign: "center" }}><span className="ak-req-count">{k.requestCount.toLocaleString()}</span></td>
                  <td>
                    <span className={`tpl-status-badge ${k.active ? "APPROVED" : "REJECTED"}`}>
                      {k.active ? "Active" : "Revoked"}
                    </span>
                  </td>
                  <td>
                    <div className="ak-wh-cell">
                      {k.webhookUrl
                        ? <span className="ak-wh-url" title={k.webhookUrl}>{k.webhookUrl.replace(/^https?:\/\//, "").slice(0, 28)}{k.webhookUrl.length > 35 ? "…" : ""}</span>
                        : <span className="ak-wh-none">Not set</span>
                      }
                      <button className="btn-sm ak-wh-cfg-btn" onClick={() => setWebhookModal({
                        id: k.id, name: k.name,
                        webhookUrl: k.webhookUrl || "",
                        webhookSecret: k.webhookSecret || "",
                        regenerateSecret: false,
                      })}>Configure</button>
                    </div>
                  </td>
                  <td>
                    {k.active && (
                      <button className="btn-delete-tpl" onClick={() => handleRevoke(k.id, k.name)}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Webhook configure modal ── */}
      {webhookModal && (
        <div className="modal-overlay" onClick={() => setWebhookModal(null)}>
          <div className="modal-box wh-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Configure Webhook</h3>
              <button className="btn-icon" onClick={() => setWebhookModal(null)}>✕</button>
            </div>

            <p className="wh-modal-sub">
              Key: <strong>{webhookModal.name}</strong> — the server will POST signed payloads to your URL for every message event.
            </p>

            <div className="input-group">
              <label>Webhook URL</label>
              <input
                type="url"
                placeholder="https://your-server.com/webhook"
                value={webhookModal.webhookUrl}
                onChange={e => setWebhookModal(m => ({ ...m, webhookUrl: e.target.value }))}
              />
            </div>

            {webhookModal.webhookSecret && (
              <div className="input-group">
                <label>Signing Secret <span style={{ fontWeight: 400, color: "var(--text-secondary)", fontSize: 11 }}>(use to verify X-MFE-Signature header)</span></label>
                <div className="ak-newkey-row">
                  <code className="ak-key-display" style={{ fontSize: 11 }}>{webhookModal.webhookSecret}</code>
                  <button className="btn-sm ak-copy-btn" onClick={() => copyText(webhookModal.webhookSecret, "Secret")}>
                    <Icons.Copy /> Copy
                  </button>
                </div>
              </div>
            )}

            <label className="wh-regen-row">
              <input type="checkbox" checked={webhookModal.regenerateSecret}
                onChange={e => setWebhookModal(m => ({ ...m, regenerateSecret: e.target.checked }))} />
              <span>{webhookModal.webhookSecret ? "Rotate signing secret (invalidates current secret)" : "Generate a signing secret"}</span>
            </label>

            <div className="modal-actions">
              {webhookModal.webhookUrl && (
                <button className="btn-sm" style={{ background: "var(--bg-sidebar)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}
                  onClick={() => handleTestWebhook(webhookModal.id)}>
                  Send Test Ping
                </button>
              )}
              {webhookModal.webhookUrl && (
                <button className="btn-sm" style={{ background: "rgba(220,50,50,.15)", color: "#f47272", border: "1px solid rgba(220,50,50,.3)" }}
                  onClick={() => { setWebhookModal(m => ({ ...m, webhookUrl: "" })); }}>
                  Remove URL
                </button>
              )}
              <button className="btn-primary" style={{ padding: "8px 22px" }} onClick={handleSaveWebhook}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// API DOCUMENTATION VIEW
// ═══════════════════════════════════════════════════════════════
const API_SERVER = API_BASE.replace("/api", "");

const DOCS_ENDPOINTS = [
  {
    method: "POST", path: "/v1/messages/send", tag: "Messages",
    summary: "Send a text message",
    desc: "Send a plain text WhatsApp message to any phone number.",
    body: { to: "919876543210", text: "Hello from Myforexeye!" },
    response: { success: true, messageId: "wamid.HBgM...", timestamp: "2026-04-10T10:00:00.000Z" },
  },
  {
    method: "POST", path: "/v1/messages/send-template", tag: "Messages",
    summary: "Send a template message",
    desc: "Send an approved WhatsApp message template with optional variable parameters.",
    body: { to: "919876543210", templateName: "hello_world", language: "en", parameters: { "1": "Rohit" }, renderedText: "Hello Rohit!" },
    response: { success: true, messageId: "wamid.HBgM...", timestamp: "2026-04-10T10:00:00.000Z" },
  },
  {
    method: "GET", path: "/v1/messages/:phone", tag: "Messages",
    summary: "Get conversation messages",
    desc: "Retrieve message history for a phone number. Supports pagination via `limit` (max 200, default 50) and `offset` query params.",
    params: [{ name: "phone", in: "path", desc: "Phone number with country code (e.g. 919876543210)" },
             { name: "limit", in: "query", desc: "Max messages to return (default 50, max 200)" },
             { name: "offset", in: "query", desc: "Skip last N messages for pagination" }],
    response: { phone: "919876543210", total: 12, messages: [{ text: "Hello!", direction: "outgoing", status: "read", timestamp: "2026-04-10T09:00:00.000Z", wa_id: "wamid.HBgM..." }] },
  },
  {
    method: "GET", path: "/v1/messages/:phone/status", tag: "Messages",
    summary: "Get message delivery summary",
    desc: "Returns aggregate delivery statistics and the last message for a contact.",
    params: [{ name: "phone", in: "path", desc: "Phone number with country code" }],
    response: { phone: "919876543210", totalMessages: 12, lastMessage: { text: "hi", direction: "outgoing", status: "read", timestamp: "2026-04-10T09:55:00.000Z" }, statuses: { sent: 1, delivered: 3, read: 7, failed: 0 } },
  },
  {
    method: "GET", path: "/v1/templates", tag: "Templates",
    summary: "List approved templates",
    desc: "Returns all WhatsApp message templates registered under your Meta Business account.",
    response: { templates: [{ id: "123456", name: "hello_world", status: "APPROVED", category: "UTILITY", language: "en", components: [] }] },
  },
  {
    method: "HOOK", path: "message.received", tag: "Webhooks",
    summary: "Incoming message received",
    desc: "Fired when a contact sends a WhatsApp message to your number. The payload is POST-ed to your configured webhook URL.",
    isWebhookEvent: true,
    response: {
      event: "message.received",
      timestamp: "2026-04-10T10:00:00.000Z",
      data: { from: "919876543210", text: "Hello!", wa_id: "wamid.HBgM...", messageTimestamp: "2026-04-10T10:00:00.000Z" }
    },
  },
  {
    method: "HOOK", path: "message.sent", tag: "Webhooks",
    summary: "Outgoing message sent",
    desc: "Fired when a message is successfully submitted to the WhatsApp API (via dashboard or /v1/ API).",
    isWebhookEvent: true,
    response: {
      event: "message.sent",
      timestamp: "2026-04-10T10:01:00.000Z",
      data: { to: "919876543210", text: "Reply from agent", wa_id: "wamid.HBgM...", messageTimestamp: "2026-04-10T10:01:00.000Z" }
    },
  },
  {
    method: "HOOK", path: "message.status_update", tag: "Webhooks",
    summary: "Message delivery status changed",
    desc: "Fired when WhatsApp delivers a status update (sent → delivered → read) for an outgoing message.",
    isWebhookEvent: true,
    response: {
      event: "message.status_update",
      timestamp: "2026-04-10T10:02:00.000Z",
      data: { wa_id: "wamid.HBgM...", status: "read", recipientPhone: "919876543210" }
    },
  },
];

function MethodBadge({ method }) {
  const colors = { GET: "#26a9e0", POST: "#00c48c", DELETE: "#ea4335", PATCH: "#ff9500", HOOK: "#8b5cf6" };
  return (
    <span className="doc-method-badge" style={{ background: colors[method] || "#667781" }}>
      {method === "HOOK" ? "EVENT" : method}
    </span>
  );
}

function CodeBlock({ code, lang = "bash" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="doc-code-block">
      <div className="doc-code-header">
        <span className="doc-code-lang">{lang}</span>
        <button className="doc-code-copy" onClick={copy}><Icons.Copy /> {copied ? "Copied!" : "Copy"}</button>
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

function ApiDocsView() {
  const [activeEndpoint, setActiveEndpoint] = useState(0);
  const ep = DOCS_ENDPOINTS[activeEndpoint];

  const curlExample = (ep) => {
    if (ep.method === "GET") {
      const path = ep.path.replace(":phone", "919876543210");
      return `curl -X GET "${API_SERVER}${path}" \\\n  -H "X-API-Key: mfe_live_your_api_key_here"`;
    }
    return `curl -X POST "${API_SERVER}${ep.path}" \\\n  -H "X-API-Key: mfe_live_your_api_key_here" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(ep.body, null, 2)}'`;
  };

  const jsExample = (ep) => {
    if (ep.method === "GET") {
      const path = ep.path.replace(":phone", "919876543210");
      return `const response = await fetch("${API_SERVER}${path}", {\n  headers: { "X-API-Key": "mfe_live_your_api_key_here" }\n});\nconst data = await response.json();\nconsole.log(data);`;
    }
    return `const response = await fetch("${API_SERVER}${ep.path}", {\n  method: "POST",\n  headers: {\n    "X-API-Key": "mfe_live_your_api_key_here",\n    "Content-Type": "application/json"\n  },\n  body: JSON.stringify(${JSON.stringify(ep.body, null, 2)})\n});\nconst data = await response.json();\nconsole.log(data);`;
  };

  const sigVerifyExample = `const crypto = require("crypto");

// Express.js example
app.post("/webhook", express.json(), (req, res) => {
  const sig  = req.headers["x-mfe-signature"] || "";
  const body = JSON.stringify(req.body);
  const expected = "sha256=" + crypto
    .createHmac("sha256", process.env.MFE_WEBHOOK_SECRET)
    .update(body)
    .digest("hex");

  if (sig !== expected) {
    return res.status(401).send("Invalid signature");
  }

  const { event, data } = req.body;
  console.log("Received event:", event, data);
  res.sendStatus(200);
});`;

  const tags = [...new Set(DOCS_ENDPOINTS.map(e => e.tag))];

  return (
    <div className="doc-root">
      {/* Left sidebar: endpoint list */}
      <div className="doc-sidebar">
        <div className="doc-sidebar-brand">
          <img src="https://myforexeye.com/hs-fs/hubfs/logo%20new.png?width=300&height=89&name=logo%20new.png"
            alt="Myforexeye" style={{ height: 28, objectFit: "contain", marginBottom: 4 }} />
          <p className="doc-version">WhatsApp API · v1</p>
        </div>

        <div className="doc-sidebar-section">
          <p className="doc-sidebar-label">Base URL</p>
          <code className="doc-base-url">{API_SERVER}</code>
        </div>

        <div className="doc-sidebar-section">
          <p className="doc-sidebar-label">Authentication</p>
          <p className="doc-sidebar-hint">Pass your API key in every request:</p>
          <code className="doc-base-url" style={{ fontSize: 10 }}>X-API-Key: mfe_live_…</code>
        </div>

        {tags.map(tag => (
          <div key={tag} className="doc-sidebar-section">
            <p className="doc-sidebar-label">{tag}</p>
            {DOCS_ENDPOINTS.filter(e => e.tag === tag).map((e, i) => {
              const idx = DOCS_ENDPOINTS.indexOf(e);
              return (
                <button key={i} className={`doc-nav-btn ${activeEndpoint === idx ? "active" : ""}`}
                  onClick={() => setActiveEndpoint(idx)}>
                  <MethodBadge method={e.method} />
                  <span className="doc-nav-path">{e.path}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Right: endpoint detail */}
      <div className="doc-content">
        <div className="doc-endpoint-header">
          <MethodBadge method={ep.method} />
          <code className="doc-endpoint-path">{ep.path}</code>
        </div>
        <h2 className="doc-endpoint-title">{ep.summary}</h2>
        <p className="doc-endpoint-desc">{ep.desc}</p>

        {ep.isWebhookEvent ? (
          <>
            {/* Webhook: how to configure */}
            <div className="doc-auth-note" style={{ borderColor: "rgba(38,169,224,.4)", background: "rgba(38,169,224,.06)" }}>
              <strong>Setup:</strong> Configure a webhook URL for any API key in <strong>Admin → API Keys → Configure</strong>.
              The server will POST this payload to your URL. You may optionally enable a signing secret to verify authenticity.
            </div>

            {/* Payload */}
            <div className="doc-section">
              <h3 className="doc-section-title">Payload sent to your URL</h3>
              <CodeBlock code={JSON.stringify(ep.response, null, 2)} lang="json" />
            </div>

            {/* Headers */}
            <div className="doc-section">
              <h3 className="doc-section-title">Request Headers</h3>
              <table className="doc-params-table">
                <thead><tr><th>Header</th><th>Value</th></tr></thead>
                <tbody>
                  <tr><td><code>X-MFE-Event</code></td><td style={{ color: "var(--text-secondary)", fontSize: 13 }}>{ep.path}</td></tr>
                  <tr><td><code>X-MFE-Signature</code></td><td style={{ color: "var(--text-secondary)", fontSize: 13 }}>sha256=HMAC_SHA256(body, webhookSecret) — present only when secret is configured</td></tr>
                  <tr><td><code>Content-Type</code></td><td style={{ color: "var(--text-secondary)", fontSize: 13 }}>application/json</td></tr>
                </tbody>
              </table>
            </div>

            {/* Signature verification */}
            <div className="doc-section">
              <h3 className="doc-section-title">Signature Verification (Node.js)</h3>
              <CodeBlock code={sigVerifyExample} lang="JavaScript" />
            </div>
          </>
        ) : (
          <>
            {/* Parameters */}
            {ep.params && (
              <div className="doc-section">
                <h3 className="doc-section-title">Parameters</h3>
                <table className="doc-params-table">
                  <thead><tr><th>Name</th><th>In</th><th>Description</th></tr></thead>
                  <tbody>
                    {ep.params.map((p, i) => (
                      <tr key={i}>
                        <td><code>{p.name}</code></td>
                        <td><span className="doc-in-badge">{p.in}</span></td>
                        <td style={{ color: "var(--text-secondary)", fontSize: 13 }}>{p.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Request body */}
            {ep.body && (
              <div className="doc-section">
                <h3 className="doc-section-title">Request Body</h3>
                <CodeBlock code={JSON.stringify(ep.body, null, 2)} lang="json" />
              </div>
            )}

            {/* Response */}
            <div className="doc-section">
              <h3 className="doc-section-title">Response</h3>
              <CodeBlock code={JSON.stringify(ep.response, null, 2)} lang="json" />
            </div>

            {/* Examples */}
            <div className="doc-section">
              <h3 className="doc-section-title">Examples</h3>
              <div className="doc-tabs">
                <CodeBlock code={curlExample(ep)} lang="cURL" />
                <CodeBlock code={jsExample(ep)} lang="JavaScript" />
              </div>
            </div>

            {/* Auth note */}
            <div className="doc-auth-note">
              <strong>Authentication:</strong> All requests require an <code>X-API-Key</code> header with a valid API key.
              Generate keys in <strong>Admin → API Keys</strong>. Keys can also be passed as <code>?api_key=</code> query parameter.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AGENTS MANAGEMENT VIEW (Admin only)
// ═══════════════════════════════════════════════════════════════
const PERMISSION_LABELS = {
  manage_star_rules: { label: "Manage Star Rules", desc: "Configure automatic lead starring for all chats" },
  view_message:     { label: "View Messages",          desc: "Read conversations" },
  send_message:     { label: "Send Messages",          desc: "Send text messages" },
  view_template:    { label: "View Templates",         desc: "Browse template list" },
  send_template:    { label: "Send Templates",         desc: "Send template messages" },
  create_template:  { label: "Create Templates",       desc: "Submit new templates to Meta" },
  add_phone:        { label: "Add Phone Number",       desc: "Add new WhatsApp numbers via Embedded Signup" },
  update_wa_profile:{ label: "Edit WA Business Profile", desc: "Update about, description, website, etc." },
  update_profile:   { label: "Edit Own Profile",       desc: "Change display name, email, password" },
  view_campaign:    { label: "View Campaigns",         desc: "View campaign dashboard and delivery stats" },
  send_campaign:    { label: "Send Campaigns",         desc: "Create and send WhatsApp campaigns to HubSpot lists" },
};

function AgentsView() {
  const [agents, setAgents]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [phones, setPhones]         = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editAgent, setEditAgent]   = useState(null);   // agent being edited (permissions)
  const [pwdAgent, setPwdAgent]     = useState(null);   // agent password reset
  const [phoneAgent, setPhoneAgent] = useState(null);   // agent phone assignment
  const [msg, setMsg]               = useState({ text: "", ok: true });

  // Create form state
  const [form, setForm] = useState({ username: "", password: "", displayName: "", email: "", permissions: [] });
  const [saving, setSaving] = useState(false);

  const fetchAgents = async () => {
    setLoading(true);
    const data = await apiFetch("/admin/agents");
    setAgents(data.agents || []);
    setLoading(false);
  };

  const fetchPhones = async () => {
    const data = await apiFetch("/phones");
    setPhones(data.phones || []);
  };

  useEffect(() => { fetchAgents(); fetchPhones(); }, []);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: "", ok: true }), 3500); };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = await apiFetch("/admin/agents", {
      method: "POST",
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (data.success) {
      flash("Agent created successfully.");
      setShowCreate(false);
      setForm({ username: "", password: "", displayName: "", email: "", permissions: [] });
      fetchAgents();
    } else {
      flash(data.error || "Failed to create agent", false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete agent "${username}"? This cannot be undone.`)) return;
    const data = await apiFetch(`/admin/agents/${username}`, { method: "DELETE" });
    if (data.success) { flash("Agent deleted."); fetchAgents(); }
    else flash(data.error || "Delete failed", false);
  };

  const handleSavePermissions = async (username, permissions) => {
    const data = await apiFetch(`/admin/agents/${username}/permissions`, {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    });
    if (data.success) { flash("Permissions updated."); setEditAgent(null); fetchAgents(); }
    else flash(data.error || "Failed to update permissions", false);
  };

  const handleSavePhones = async (username, assignedPhones) => {
    const data = await apiFetch(`/admin/agents/${username}/phones`, {
      method: "PUT",
      body: JSON.stringify({ assignedPhones }),
    });
    if (data.success) { flash("Phone assignment updated."); setPhoneAgent(null); fetchAgents(); }
    else flash(data.error || "Failed to update phones", false);
  };

  const handleResetPassword = async (username, newPassword) => {
    if (newPassword.length < 6) { flash("Password must be at least 6 characters", false); return; }
    const data = await apiFetch(`/admin/agents/${username}/password`, {
      method: "PUT",
      body: JSON.stringify({ newPassword }),
    });
    if (data.success) { flash("Password reset successfully."); setPwdAgent(null); }
    else flash(data.error || "Failed to reset password", false);
  };

  const togglePerm = (perms, perm) =>
    perms.includes(perm) ? perms.filter(p => p !== perm) : [...perms, perm];

  return (
    <div className="am-root">
      <div className="am-header">
        <div>
          <h2>Agent Management</h2>
          <p className="wap-subtitle">Create agents and assign their access permissions</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(v => !v)}>
          {showCreate ? "Cancel" : "+ New Agent"}
        </button>
      </div>

      {msg.text && <div className={`wap-alert ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>}

      {/* ── Create Agent Form ── */}
      {showCreate && (
        <form className="am-create-form" onSubmit={handleCreate}>
          <h3>Create New Agent</h3>
          <div className="am-create-row">
            <div className="input-group">
              <label>Username <span className="ct-req">*</span></label>
              <input placeholder="e.g. john_doe" value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))} required />
              <span className="ct-hint-text">Lowercase, numbers, underscores only</span>
            </div>
            <div className="input-group">
              <label>Password <span className="ct-req">*</span></label>
              <input type="password" placeholder="Min 6 characters" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
            </div>
            <div className="input-group">
              <label>Display Name</label>
              <input placeholder="Full name" value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
            </div>
            <div className="input-group">
              <label>Email</label>
              <input type="email" placeholder="agent@email.com" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <div className="am-perm-section">
            <label>Permissions</label>
            <div className="am-perm-grid">
              {Object.entries(PERMISSION_LABELS).map(([key, { label, desc }]) => (
                <label key={key} className={`am-perm-check ${form.permissions.includes(key) ? "checked" : ""}`}>
                  <input type="checkbox" checked={form.permissions.includes(key)}
                    onChange={() => setForm(f => ({ ...f, permissions: togglePerm(f.permissions, key) }))} />
                  <span className="am-perm-name">{label}</span>
                  <span className="am-perm-desc">{desc}</span>
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="btn-primary" disabled={saving || !form.username || !form.password}>
            {saving ? "Creating..." : "Create Agent"}
          </button>
        </form>
      )}

      {/* ── Agents Table ── */}
      {loading ? (
        <div className="template-loading">Loading agents...</div>
      ) : agents.length === 0 ? (
        <div className="template-loading">No agents yet. Create one above.</div>
      ) : (
        <div className="am-table-wrap">
          <table className="tpl-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Permissions</th>
                <th>Assigned Numbers</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map(a => (
                <tr key={a.username} className="tpl-row" style={{ cursor: "default" }}>
                  <td>
                    <div className="am-agent-cell">
                      <div className="am-agent-avatar">{(a.displayName || a.username)[0].toUpperCase()}</div>
                      <div>
                        <span className="am-agent-name">{a.displayName || a.username}</span>
                        <span className="am-agent-username">@{a.username}</span>
                        {a.email && <span className="am-agent-email">{a.email}</span>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="am-perm-tags">
                      {(a.permissions || []).length === 0
                        ? <span className="am-no-perms">No permissions</span>
                        : (a.permissions || []).map(p => (
                          <span key={p} className="am-perm-tag">{PERMISSION_LABELS[p]?.label || p}</span>
                        ))
                      }
                    </div>
                  </td>
                  <td>
                    <span className="am-phones-cell">
                      {(a.assignedPhones || []).length === 0
                        ? <span className="am-no-perms">All numbers</span>
                        : (a.assignedPhones || []).map(pid => {
                            const ph = phones.find(p => p.id === pid);
                            return <span key={pid} className="am-perm-tag">{ph?.display_phone_number || pid}</span>;
                          })
                      }
                    </span>
                  </td>
                  <td>
                    <div className="am-actions">
                      <button className="btn-sm" title="Edit permissions" onClick={() => setEditAgent({ ...a, draftPerms: [...(a.permissions || [])] })}>
                        <Icons.Shield /> Roles
                      </button>
                      <button className="btn-sm" title="Assign phone numbers" onClick={() => setPhoneAgent({ ...a, draftPhones: [...(a.assignedPhones || [])] })}>
                        <Icons.Phone /> Phones
                      </button>
                      <button className="btn-sm" title="Reset password" onClick={() => setPwdAgent({ username: a.username, newPwd: "" })}>
                        <Icons.Key /> Password
                      </button>
                      <button className="btn-delete-tpl" title="Delete agent" onClick={() => handleDelete(a.username)}>
                        <Icons.Trash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit Permissions Modal ── */}
      {editAgent && (
        <div className="tpl-modal-overlay" onClick={() => setEditAgent(null)}>
          <div className="tpl-modal am-modal" onClick={e => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div>
                <h3>Edit Permissions</h3>
                <span className="tpl-modal-meta">@{editAgent.username}</span>
              </div>
              <button className="btn-icon" onClick={() => setEditAgent(null)}>✕</button>
            </div>
            <div className="am-perm-grid" style={{ padding: "16px" }}>
              {Object.entries(PERMISSION_LABELS).map(([key, { label, desc }]) => (
                <label key={key} className={`am-perm-check ${editAgent.draftPerms.includes(key) ? "checked" : ""}`}>
                  <input type="checkbox" checked={editAgent.draftPerms.includes(key)}
                    onChange={() => setEditAgent(a => ({ ...a, draftPerms: togglePerm(a.draftPerms, key) }))} />
                  <span className="am-perm-name">{label}</span>
                  <span className="am-perm-desc">{desc}</span>
                </label>
              ))}
            </div>
            <div style={{ padding: "0 16px 16px", display: "flex", gap: 8 }}>
              <button className="btn-primary" onClick={() => handleSavePermissions(editAgent.username, editAgent.draftPerms)}>
                Save Permissions
              </button>
              <button className="btn-sm" onClick={() => setEditAgent(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Phones Modal ── */}
      {phoneAgent && (
        <div className="tpl-modal-overlay" onClick={() => setPhoneAgent(null)}>
          <div className="tpl-modal am-modal" onClick={e => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div>
                <h3>Assign Phone Numbers</h3>
                <span className="tpl-modal-meta">@{phoneAgent.username} · leave empty = access all</span>
              </div>
              <button className="btn-icon" onClick={() => setPhoneAgent(null)}>✕</button>
            </div>
            <div style={{ padding: "16px" }}>
              {phones.length === 0 ? (
                <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No phone numbers found. Add them in Phone Numbers tab first.</p>
              ) : (
                <div className="am-perm-grid">
                  {phones.map(ph => (
                    <label key={ph.id} className={`am-perm-check ${phoneAgent.draftPhones.includes(ph.id) ? "checked" : ""}`}>
                      <input type="checkbox" checked={phoneAgent.draftPhones.includes(ph.id)}
                        onChange={() => setPhoneAgent(a => ({
                          ...a,
                          draftPhones: a.draftPhones.includes(ph.id)
                            ? a.draftPhones.filter(x => x !== ph.id)
                            : [...a.draftPhones, ph.id]
                        }))} />
                      <span className="am-perm-name">{ph.display_phone_number || ph.id}</span>
                      <span className="am-perm-desc">{ph.verified_name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding: "0 16px 16px", display: "flex", gap: 8 }}>
              <button className="btn-primary" onClick={() => handleSavePhones(phoneAgent.username, phoneAgent.draftPhones)}>
                Save Assignment
              </button>
              <button className="btn-sm" onClick={() => setPhoneAgent(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      {pwdAgent && (
        <div className="tpl-modal-overlay" onClick={() => setPwdAgent(null)}>
          <div className="tpl-modal am-modal" onClick={e => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div><h3>Reset Password</h3><span className="tpl-modal-meta">@{pwdAgent.username}</span></div>
              <button className="btn-icon" onClick={() => setPwdAgent(null)}>✕</button>
            </div>
            <div style={{ padding: "16px" }}>
              <div className="input-group">
                <label>New Password</label>
                <input type="password" placeholder="Min 6 characters" value={pwdAgent.newPwd}
                  onChange={e => setPwdAgent(a => ({ ...a, newPwd: e.target.value }))} autoFocus />
              </div>
            </div>
            <div style={{ padding: "0 16px 16px", display: "flex", gap: 8 }}>
              <button className="btn-primary" onClick={() => handleResetPassword(pwdAgent.username, pwdAgent.newPwd)}
                disabled={!pwdAgent.newPwd}>
                Set Password
              </button>
              <button className="btn-sm" onClick={() => setPwdAgent(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [authed, setAuthed]           = useState(!!getToken());
  const [currentUser, setCurrentUser] = useState(getUser());
  const [contacts, setContacts]       = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [showCreate, setShowCreate]   = useState(false);
  const [allPhones, setAllPhones]     = useState([]);
  const [fromPhoneId, setFromPhoneId] = useState(null); // which number to send from
  const validTabs = ["chats","templates","campaigns","workflows","phones","wa-profile","apikeys","agents"];
  const hashTab = window.location.hash.replace("#", "");
  const [tab, setTabState]            = useState(validTabs.includes(hashTab) ? hashTab : "chats");
  const setTab = (t) => { setTabState(t); window.location.hash = t; };
  const [showProfile, setShowProfile] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [pendingStars, setPendingStars] = useState({});
  const [starError, setStarError] = useState("");
  const starRequests = useRef(new Set());
  const contactVersion = useRef(0);

  const chatHistory = useChatMessages(activeContact?.phone, authed && tab === "chats", apiFetch);
  const { messages, refresh: fetchMessages } = chatHistory;

  const fetchContacts = useCallback(async () => {
    const version = contactVersion.current;
    const data = await apiFetch("/contacts");
    if (data.contacts && version === contactVersion.current && starRequests.current.size === 0) setContacts(data.contacts);
  }, []);

  const fetchPhones = useCallback(async () => {
    const data = await apiFetch("/phones");
    if (data.phones) {
      setAllPhones(data.phones);
      // Auto-select first phone if nothing selected yet
      setFromPhoneId(prev => prev || data.phones[0]?.phone_number_id || null);
    }
  }, []);

  useEffect(() => {
    if (authed) {
      fetchContacts();
      fetchPhones();
      const interval = setInterval(fetchContacts, 10000);
      return () => clearInterval(interval);
    }
  }, [authed, fetchContacts, fetchPhones]);

  const handleSend = async (text, mediaUrl) => {
    await apiFetch("/messages/send", {
      method: "POST",
      body: JSON.stringify({ to: activeContact.phone, text, from_phone_id: fromPhoneId, ...(mediaUrl ? { mediaUrl } : {}) }),
    });
    fetchMessages();
  };

  const handleSendTemplate = async (template, params) => {
    const renderedText = renderTemplate(template, params);
    await apiFetch("/messages/send-template", {
      method: "POST",
      body: JSON.stringify({
        to: activeContact.phone,
        templateName: template.name,
        language: template.language,
        parameters: params,
        renderedText,
        from_phone_id: fromPhoneId,
      }),
    });
    fetchMessages();
  };

  const handleNewChat = (phone) => {
    // Normalize: strip all non-digits so +91... and 91... map to the same entry
    const normalized = phone.replace(/\D/g, "");
    if (!normalized) return;
    const existing = contacts.find(c => c.phone === normalized);
    if (existing) {
      setActiveContact(existing);
    } else {
      const newContact = { phone: normalized, name: normalized, lastMessage: "", unread: 0 };
      setContacts([newContact, ...contacts]);
      setActiveContact(newContact);
    }
  };

  const handleToggleStar = async contact => {
    const phone = contact.phone;
    if (starRequests.current.has(phone)) return;
    starRequests.current.add(phone);
    contactVersion.current++;
    setPendingStars(prev => ({ ...prev, [phone]: true }));
    setStarError("");
    try {
      const data = await apiFetch(`/contacts/${encodeURIComponent(phone)}/star`, {
        method: "PATCH", body: JSON.stringify({ starred: !contact.starred }),
      });
      if (!data.success) throw new Error(data.error || "Unable to update star.");
      setContacts(prev => prev.map(c => c.phone === phone ? { ...c, ...data.contact } : c));
      setActiveContact(prev => prev?.phone === phone ? { ...prev, ...data.contact } : prev);
    } catch (err) { setStarError(err.message || "Unable to update star. Please try again."); }
    finally {
      starRequests.current.delete(phone);
      contactVersion.current++;
      setPendingStars(prev => ({ ...prev, [phone]: false }));
    }
  };

  const handleToggleOptOut = async (phone, opted_out) => {
    await apiFetch(`/contacts/${phone}/optout`, {
      method: "PATCH",
      body: JSON.stringify({ opted_out }),
    });
    setContacts(prev => prev.map(c => c.phone === phone ? { ...c, opted_out, optedOutAt: opted_out ? (c.optedOutAt || new Date().toISOString()) : null } : c));
    if (activeContact?.phone === phone) {
      setActiveContact(prev => ({ ...prev, opted_out, optedOutAt: opted_out ? (prev.optedOutAt || new Date().toISOString()) : null }));
    }
  };

  const handleLogout = () => {
    clearToken();
    clearUser();
    setAuthed(false);
    setCurrentUser(null);
  };

  if (!authed) return <LoginScreen onLogin={(user) => { setAuthed(true); setCurrentUser(user); }} />;

  return (
    <div className={`app-root${mobileShowChat ? " mobile-chat-active" : ""}`}>
      {showProfile && <ProfilePanel onClose={() => setShowProfile(false)} />}

      {/* Sidebar Navigation */}
      <nav className="sidebar-nav">
        <div className="nav-logo">
          <img
            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAYCAMAAADeQm2wAAAAxlBMVEX////5+vzW2OPc3ef4+v3b2+PLzNjK1+rs8Pfp6e+Ul7PJ0uXh5vBcYI22wtz0///N7/txdZrDydpuib2l4vi7vc50eqBLcbKQ2fYqMHN9lL+irc+D2Pjq+P1LXJI2Yqy05fkAAGImV6F1n8tRzPYdNn8UVKY0uOkAEWcbTpwmhcSgzOlBSYIaXasuwfG9zeSvsccvmNFPp9nW9/8AaLNiaZUcJm8dqOAAQJcPHm0jQopbyvMANI6FiKoDJ3dNUYQ2QH43ruBZpZpQAAAA1UlEQVR4AXWRAxYDQRBEJxvbdjKMvdb9DxUny66HwW83ApVAsKRkCmTpTDYHsXyhWALzlbOVag2A9UYTtQDW7nQTPQSoPxiOxgDrTKYzKGhpvlji7z3iXyaU/SqthZqVuFjhf0/rINyQ7W72f832fnbg4ohT3vuU9+65Ptlif4/ni0evNyHvAoMbKekfU7UL1lFAzDCfzimprD7b2M2CcCwrll12+E17VrNDIc1kKgjRBF1hz9Gj7EIp3cp4p6Ooath1Gca7Xew6Ur0n2fWqCFAqlQq8HyqnFTSwoHuCAAAAAElFTkSuQmCC"
            alt="MFE"
            style={{ width: 28, height: 28, objectFit: "contain" }}
            title="Myforexeye"
          />
        </div>

        {/* Chats — requires view_message */}
        {can(currentUser, "view_message") && (
          <button className={`nav-btn ${tab === "chats" ? "active" : ""}`} onClick={() => { setTab("chats"); setMobileShowChat(false); }} title="Chats">
            <Icons.Chat />
          </button>
        )}

        {/* Templates — requires view_template */}
        {can(currentUser, "view_template") && (
          <button className={`nav-btn ${tab === "templates" ? "active" : ""}`} onClick={() => setTab("templates")} title="Templates">
            <Icons.Template />
          </button>
        )}

        {/* Campaigns — requires view_campaign or send_campaign */}
        {(can(currentUser, "view_campaign") || can(currentUser, "send_campaign")) && (
          <button className={`nav-btn ${tab === "campaigns" ? "active" : ""}`} onClick={() => setTab("campaigns")} title="Campaigns">
            <Icons.Campaign />
          </button>
        )}

        {/* Workflows — requires send_campaign or admin */}
        {(can(currentUser, "send_campaign") || currentUser?.role === "admin") && (
          <button className={`nav-btn ${tab === "workflows" ? "active" : ""}`} onClick={() => setTab("workflows")} title="Workflow Automation">
            <Icons.Workflow />
          </button>
        )}

        <div className="nav-spacer" />

        {/* Phone Numbers — requires add_phone or admin */}
        {can(currentUser, "add_phone") && (
          <button className={`nav-btn ${tab === "phones" ? "active" : ""}`} onClick={() => setTab("phones")} title="Phone Numbers">
            <Icons.Phone />
          </button>
        )}

        {/* WA Business Profile — requires update_wa_profile or admin */}
        {can(currentUser, "update_wa_profile") && (
          <button className={`nav-btn ${tab === "wa-profile" ? "active" : ""}`} onClick={() => setTab("wa-profile")} title="WhatsApp Business Profile">
            <Icons.WAProfile />
          </button>
        )}

        {/* API Keys — admin only */}
        {currentUser?.role === "admin" && (
          <button className={`nav-btn ${tab === "apikeys" ? "active" : ""}`} onClick={() => setTab("apikeys")} title="API Keys">
            <Icons.ApiKey />
          </button>
        )}

        {/* Agents — admin only */}
        {currentUser?.role === "admin" && (
          <button className={`nav-btn ${tab === "agents" ? "active" : ""}`} onClick={() => setTab("agents")} title="Agent Management">
            <Icons.Agents />
          </button>
        )}

        <button className={`nav-btn ${showProfile ? "active" : ""}`} onClick={() => setShowProfile(v => !v)} title="My Profile">
          <Icons.Profile />
        </button>
        <button className="nav-btn logout" onClick={handleLogout} title="Logout">
          <Icons.Logout />
        </button>
      </nav>

      {/* Contact List */}
      {tab === "chats" && can(currentUser, "view_message") && (
        <ContactList
          contacts={contacts}
          onToggleStar={handleToggleStar}
          pendingStars={pendingStars}
          starError={starError}
          canManageStarRules={can(currentUser, "manage_star_rules")}
          activeContact={activeContact}
          onSelect={(c) => {
            setActiveContact(c);
            setMobileShowChat(true);
            if ((c.unread || 0) > 0) {
              apiFetch(`/contacts/${c.phone}/read`, { method: "POST" });
              setContacts(prev => prev.map(x => x.phone === c.phone ? { ...x, unread: 0 } : x));
            }
          }}
          onNewChat={(phone) => { handleNewChat(phone); setMobileShowChat(true); }}
        />
      )}

      {/* Create Template — full width */}
      {tab === "templates" && can(currentUser, "view_template") && showCreate && can(currentUser, "create_template") && (
        <CreateTemplateView onBack={() => setShowCreate(false)} />
      )}

      {/* Templates List Panel */}
      {tab === "templates" && can(currentUser, "view_template") && !showCreate && (
        <div className="templates-full">
          <TemplatesView
            canCreate={can(currentUser, "create_template")}
            canDelete={currentUser?.role === "admin"}
            onCreate={() => setShowCreate(true)}
          />
        </div>
      )}

      {/* Campaigns */}
      {tab === "campaigns" && (can(currentUser, "view_campaign") || can(currentUser, "send_campaign")) && (
        <div className="templates-full" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <CampaignsView />
        </div>
      )}

      {/* Workflow Automation */}
      {tab === "workflows" && (can(currentUser, "send_campaign") || currentUser?.role === "admin") && (
        <div className="templates-full" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <WorkflowsTab />
        </div>
      )}

      {/* Phone Numbers */}
      {tab === "phones" && can(currentUser, "add_phone") && (
        <div className="templates-full" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <PhoneNumbersView />
        </div>
      )}

      {/* WA Business Profile */}
      {tab === "wa-profile" && can(currentUser, "update_wa_profile") && (
        <div className="templates-full" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <WAProfileView />
        </div>
      )}

      {/* API Keys — admin only */}
      {tab === "apikeys" && currentUser?.role === "admin" && (
        <div className="templates-full" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <ApiKeysView />
        </div>
      )}

      {/* Agent Management — admin only */}
      {tab === "agents" && currentUser?.role === "admin" && (
        <div className="templates-full" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <AgentsView />
        </div>
      )}

      {/* Chat Window — only on chats tab */}
      {tab === "chats" && can(currentUser, "view_message") && (
        <div className="chat-workspace">
        <ChatWindow
          contact={contacts.find(c => c.phone === activeContact?.phone) || activeContact}
          onToggleStar={handleToggleStar}
          pendingStars={pendingStars}
          starError={starError}
          messages={messages}
          history={chatHistory}
          onSend={can(currentUser, "send_message") ? handleSend : null}
          onSendTemplate={can(currentUser, "send_template") ? handleSendTemplate : null}
          canSend={can(currentUser, "send_message")}
          canSendTemplate={can(currentUser, "send_template")}
          onBack={() => setMobileShowChat(false)}
          phones={allPhones}
          fromPhoneId={fromPhoneId}
          onFromPhoneChange={setFromPhoneId}
          onToggleOptOut={handleToggleOptOut}
          isAdmin={currentUser?.role === "admin"}
        />
        {activeContact && <CustomerDetails phone={activeContact.phone} apiFetch={apiFetch} />}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CREATE TEMPLATE VIEW
// ═══════════════════════════════════════════════════════════════
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "en_US", label: "English (US)" },
  { code: "hi", label: "Hindi" },
  { code: "ar", label: "Arabic" },
  { code: "es", label: "Spanish" },
  { code: "pt_BR", label: "Portuguese (BR)" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "id", label: "Indonesian" },
  { code: "ru", label: "Russian" },
  { code: "zh_CN", label: "Chinese (Simplified)" },
];

const EMPTY_FORM = {
  name: "", category: "MARKETING", language: "en",
  headerType: "NONE", headerText: "", headerMediaUrl: "",
  body: "", footer: "", buttons: [],
  sampleValues: {}, // { "1": "John", "2": "ORD-123" } — sample values for {{1}}, {{2}}, …
};

function extractVarNums(text) {
  const matches = (text || "").match(/{{\d+}}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}]/g, "")))].sort((a, b) => Number(a) - Number(b));
}

function CreateTemplateView({ onBack }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { name, status, id, pollCount }
  const [error, setError] = useState("");

  // Poll Meta every 2 minutes until APPROVED or REJECTED
  useEffect(() => {
    if (!submitted || submitted.status === "APPROVED" || submitted.status === "REJECTED") return;
    const interval = setInterval(async () => {
      const data = await apiFetch("/templates");
      const found = (data.templates || []).find(t => t.name === submitted.name);
      if (found) {
        setSubmitted(prev => ({ ...prev, status: found.status, pollCount: (prev.pollCount || 0) + 1 }));
      }
    }, 120000);
    return () => clearInterval(interval);
  }, [submitted]);

  const buildComponents = () => {
    const comps = [];

    if (form.headerType === "TEXT" && form.headerText.trim()) {
      const hdrVars = extractVarNums(form.headerText);
      const hdrComp = { type: "HEADER", format: "TEXT", text: form.headerText.trim() };
      if (hdrVars.length > 0) {
        const samples = hdrVars.map(n => form.sampleValues[n] || `sample${n}`);
        hdrComp.example = { header_text: samples };
      }
      comps.push(hdrComp);
    } else if ((form.headerType === "IMAGE" || form.headerType === "VIDEO" || form.headerType === "DOCUMENT") && form.headerMediaUrl.trim()) {
      comps.push({
        type: "HEADER",
        format: form.headerType,
        example: { header_handle: [form.headerMediaUrl.trim()] },
      });
    }

    if (form.body.trim()) {
      const bodyVars = extractVarNums(form.body);
      const bodyComp = { type: "BODY", text: form.body.trim() };
      if (bodyVars.length > 0) {
        const samples = bodyVars.map(n => form.sampleValues[n] || `sample${n}`);
        bodyComp.example = { body_text: [samples] };
      }
      comps.push(bodyComp);
    }

    if (form.footer.trim())
      comps.push({ type: "FOOTER", text: form.footer.trim() });
    if (form.buttons.length > 0) {
      const buttons = form.buttons.map(btn => {
        if (btn.type === "URL") {
          const url = btn.dynamic ? `${btn.baseUrl.trim()}{{1}}` : btn.url;
          const b = { type: "URL", text: btn.text, url };
          if (btn.dynamic && btn.exampleValue?.trim()) b.example = [btn.exampleValue.trim()];
          return b;
        }
        if (btn.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: btn.text, phone_number: btn.phone_number };
        return                                  { type: "QUICK_REPLY",  text: btn.text };
      });
      comps.push({ type: "BUTTONS", buttons });
    }
    return comps;
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return setError("Template name is required.");
    if (!/^[a-z0-9_]+$/.test(form.name)) return setError("Name must be lowercase letters, numbers, and underscores only.");
    if (!form.body.trim()) return setError("Message body is required.");
    for (const btn of form.buttons) {
      if (!btn.text?.trim()) return setError(`One of your buttons has no label text. Please fill in all button labels.`);
      if (btn.type === "URL" && !btn.dynamic && !btn.url?.trim()) return setError(`URL button "${btn.text}" has no URL. Please enter a URL.`);
      if (btn.type === "URL" && btn.dynamic && !btn.baseUrl?.trim()) return setError(`URL button "${btn.text}" needs a base URL for the dynamic link.`);
      if (btn.type === "URL" && btn.dynamic && !btn.exampleValue?.trim()) return setError(`URL button "${btn.text}" needs an example value (required by Meta for review).`);
      if (btn.type === "PHONE_NUMBER" && !btn.phone_number?.trim()) return setError(`Phone button "${btn.text}" has no phone number.`);
    }
    setError("");
    setSubmitting(true);
    const data = await apiFetch("/templates/create", {
      method: "POST",
      body: JSON.stringify({ name: form.name, category: form.category, language: form.language, components: buildComponents() }),
    });
    setSubmitting(false);
    if (data.success) {
      setSubmitted({ name: form.name, status: data.status || "PENDING", id: data.id, pollCount: 0 });
    } else {
      const metaErr = data.details?.error;
      const msg = metaErr?.error_user_msg || metaErr?.message || data.error || "Failed to create template.";
      const detail = metaErr?.error_data ? `\nDetail: ${JSON.stringify(metaErr.error_data)}` : "";
      setError(msg + detail);
    }
  };

  const addVariable = () => {
    const count = (form.body.match(/{{\d+}}/g) || []).length;
    setForm(f => ({ ...f, body: f.body + `{{${count + 1}}}` }));
  };

  const addButton = (type) => {
    setError("");
    const btn = type === "QUICK_REPLY" ? { type, text: "" }
      : type === "URL" ? { type, text: "", url: "", dynamic: false, baseUrl: "", exampleValue: "" }
      : { type: "PHONE_NUMBER", text: "", phone_number: "" };
    setForm(f => ({ ...f, buttons: [...f.buttons, btn] }));
  };

  const updateButton = (i, field, val) =>
    setForm(f => { const b = [...f.buttons]; b[i] = { ...b[i], [field]: val }; return { ...f, buttons: b }; });

  const removeButton = (i) =>
    setForm(f => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));

  // Submitted state — show status card with poll countdown
  if (submitted) {
    const icon = submitted.status === "APPROVED" ? "✓" : submitted.status === "REJECTED" ? "✕" : "⏳";
    return (
      <div className="ct-submitted">
        <div className="ct-submitted-card">
          <div className={`ct-status-icon ${submitted.status}`}>{icon}</div>
          <h2>Template Submitted</h2>
          <p className="ct-submitted-name">{submitted.name}</p>
          <span className={`template-status ${submitted.status}`}>{submitted.status}</span>
          {submitted.status === "PENDING" && (
            <p className="ct-hint">
              Meta is reviewing your template. Status checks automatically every 2 minutes.
              {submitted.pollCount > 0 && <span className="ct-poll"> Checked {submitted.pollCount}×</span>}
            </p>
          )}
          {submitted.status === "APPROVED" && <p className="ct-hint approved">Template approved and ready to use!</p>}
          {submitted.status === "REJECTED" && <p className="ct-hint rejected">Rejected by Meta. Review their content guidelines and try again.</p>}
          <div className="ct-submitted-actions">
            <button className="btn-primary" onClick={onBack}>Back to Templates</button>
            <button className="btn-sm" onClick={() => { setSubmitted(null); setForm(EMPTY_FORM); }}>Create Another</button>
          </div>
        </div>
      </div>
    );
  }

  const allVarNums = [...new Set([...extractVarNums(form.headerText), ...extractVarNums(form.body)])].sort((a, b) => Number(a) - Number(b));

  const previewBody = form.body.replace(/{{\d+}}/g, m => {
    const n = m.replace(/[{}]/g, "");
    return form.sampleValues[n] ? `*${form.sampleValues[n]}*` : `[{{${n}}}]`;
  });
  const previewHeader = form.headerText.replace(/{{\d+}}/g, m => {
    const n = m.replace(/[{}]/g, "");
    return form.sampleValues[n] ? form.sampleValues[n] : `[{{${n}}}]`;
  });

  return (
    <div className="ct-root">
      <div className="ct-header">
        <button className="btn-icon" onClick={onBack}>←</button>
        <h2>Create Message Template</h2>
      </div>

      <div className="ct-body">
        {/* ── FORM ── */}
        <div className="ct-form">
          {error && <div className="error-msg">{error}</div>}

          <div className="ct-section">
            <h3 className="ct-section-title">Basic Info</h3>
            <div className="input-group">
              <label>Template Name <span className="ct-req">*</span></label>
              <input
                placeholder="e.g. order_confirmation"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))}
              />
              <span className="ct-hint-text">Lowercase letters, numbers, underscores only</span>
            </div>
            <div className="ct-row">
              <div className="input-group">
                <label>Category <span className="ct-req">*</span></label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                  <option value="AUTHENTICATION">Authentication</option>
                </select>
              </div>
              <div className="input-group">
                <label>Language <span className="ct-req">*</span></label>
                <select value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="ct-section">
            <h3 className="ct-section-title">Header <span className="ct-opt">Optional</span></h3>
            <div className="input-group">
              <label>Type</label>
              <select value={form.headerType} onChange={e => setForm(f => ({ ...f, headerType: e.target.value, headerText: "", headerMediaUrl: "" }))}>
                <option value="NONE">None</option>
                <option value="TEXT">Text</option>
                <option value="IMAGE">Image</option>
                <option value="VIDEO">Video</option>
                <option value="DOCUMENT">Document</option>
              </select>
            </div>
            {form.headerType === "TEXT" && (
              <div className="input-group">
                <label>Header Text</label>
                <input placeholder="Max 60 characters" maxLength={60} value={form.headerText}
                  onChange={e => setForm(f => ({ ...f, headerText: e.target.value }))} />
              </div>
            )}
            {(form.headerType === "IMAGE" || form.headerType === "VIDEO" || form.headerType === "DOCUMENT") && (
              <div className="input-group">
                <label>{form.headerType === "IMAGE" ? "Image URL" : form.headerType === "VIDEO" ? "Video URL" : "Document URL"}</label>
                <input
                  placeholder={form.headerType === "IMAGE" ? "https://example.com/image.jpg" : form.headerType === "VIDEO" ? "https://example.com/video.mp4" : "https://example.com/document.pdf"}
                  value={form.headerMediaUrl}
                  onChange={e => setForm(f => ({ ...f, headerMediaUrl: e.target.value }))}
                />
                <span className="ct-hint-text">Meta requires a publicly accessible URL for template review. This URL is used as a sample.</span>
              </div>
            )}
          </div>

          <div className="ct-section">
            <h3 className="ct-section-title">Body <span className="ct-req">*</span></h3>
            <div className="input-group">
              <label>Message</label>
              <textarea
                placeholder={"Enter your message.\nUse {{1}}, {{2}} for dynamic variables."}
                value={form.body} rows={5} maxLength={1024}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              />
              <div className="ct-body-bar">
                <span className="ct-char">{form.body.length}/1024</span>
                <button type="button" className="btn-sm" onClick={addVariable}>+ Add Variable</button>
              </div>
            </div>
          </div>

          {/* Sample / test values — only shown when variables exist */}
          {allVarNums.length > 0 && (
            <div className="ct-section">
              <h3 className="ct-section-title">Sample Values <span className="ct-req">*</span></h3>
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
                Meta requires sample content for each variable to review your template. These are not sent to customers.
              </p>
              {allVarNums.map(n => (
                <div key={n} className="input-group sm" style={{ marginBottom: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <code style={{ background: "var(--bg-sidebar)", padding: "2px 7px", borderRadius: 4, fontSize: 12 }}>{`{{${n}}}`}</code>
                    Sample value
                  </label>
                  <input
                    placeholder={`e.g. ${n === "1" ? "John" : n === "2" ? "ORD-12345" : `Value ${n}`}`}
                    value={form.sampleValues[n] || ""}
                    onChange={e => setForm(f => ({ ...f, sampleValues: { ...f.sampleValues, [n]: e.target.value } }))}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="ct-section">
            <h3 className="ct-section-title">Footer <span className="ct-opt">Optional</span></h3>
            <div className="input-group">
              <label>Footer Text</label>
              <input placeholder="e.g. Reply STOP to opt out" maxLength={60} value={form.footer}
                onChange={e => setForm(f => ({ ...f, footer: e.target.value }))} />
            </div>
          </div>

          <div className="ct-section">
            <h3 className="ct-section-title">Buttons <span className="ct-opt">Optional — max 3</span></h3>
            {form.buttons.map((btn, i) => (
              <div key={i} className="ct-btn-row">
                <div className="ct-btn-row-header">
                  <span className="ct-btn-type">{btn.type.replace("_", " ")}</span>
                  <button className="btn-icon" onClick={() => removeButton(i)}>✕</button>
                </div>
                <div className="input-group sm">
                  <label>Button Text <span className="ct-char">(max 25)</span></label>
                  <input placeholder="Label shown on button" maxLength={25} value={btn.text}
                    onChange={e => updateButton(i, "text", e.target.value)} />
                </div>
                {btn.type === "URL" && (
                  <>
                    <div className="input-group sm">
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={btn.dynamic || false}
                          onChange={e => updateButton(i, "dynamic", e.target.checked)}
                        />
                        Dynamic URL <span style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 600 }}>{"{{1}}"}</span>
                        <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 400 }}>— for click tracking</span>
                      </label>
                    </div>
                    {btn.dynamic ? (
                      <>
                        <div className="input-group sm">
                          <label>Base URL</label>
                          <input
                            placeholder="https://whatsapp.myforexeye.com/t/"
                            value={btn.baseUrl || ""}
                            onChange={e => updateButton(i, "baseUrl", e.target.value)}
                          />
                          {btn.baseUrl?.trim() && (
                            <span style={{ fontSize: 11, color: "#8b5cf6", marginTop: 4, display: "block" }}>
                              Full URL submitted to Meta: <strong>{btn.baseUrl.trim()}{"{{1}}"}</strong>
                            </span>
                          )}
                        </div>
                        <div className="input-group sm">
                          <label>Example value <span className="ct-req">*</span></label>
                          <input
                            placeholder="e.g. sample_token_abc123"
                            value={btn.exampleValue || ""}
                            onChange={e => updateButton(i, "exampleValue", e.target.value)}
                          />
                          <span style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, display: "block" }}>
                            Meta requires a sample value for {"{{1}}"} when submitting for review
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="input-group sm">
                        <label>URL</label>
                        <input
                          placeholder="https://example.com"
                          value={btn.url || ""}
                          onChange={e => updateButton(i, "url", e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}
                {btn.type === "PHONE_NUMBER" && (
                  <div className="input-group sm">
                    <label>Phone Number</label>
                    <input placeholder="+919876543210" value={btn.phone_number}
                      onChange={e => updateButton(i, "phone_number", e.target.value)} />
                  </div>
                )}
              </div>
            ))}
            <div className="ct-btn-add-row">
              <button className="btn-sm" onClick={() => addButton("QUICK_REPLY")}>+ Quick Reply</button>
              <button className="btn-sm" onClick={() => addButton("URL")}>+ Visit URL</button>
              <button className="btn-sm" onClick={() => addButton("PHONE_NUMBER")}>+ Call Phone</button>
            </div>
          </div>

          <button className="btn-primary btn-full" onClick={handleSubmit}
            disabled={submitting || !form.name || !form.body}>
            {submitting ? "Submitting to Meta..." : "Submit for Review"}
          </button>
        </div>

        {/* ── PREVIEW ── */}
        <div className="ct-preview">
          <h3>Live Preview</h3>
          <div className="ct-phone">
            <div className="ct-bubble">
              {form.headerType === "TEXT" && form.headerText && (
                <p className="ct-preview-header">{previewHeader}</p>
              )}
              {form.headerType === "IMAGE" && (
                <div className="tpl-media-placeholder" style={{ borderRadius: 8, marginBottom: 8, background: "#e2e8f0", padding: "20px 0", textAlign: "center", fontSize: 28 }}>
                  🖼 {form.headerMediaUrl ? <img src={form.headerMediaUrl} alt="preview" style={{ maxWidth: "100%", maxHeight: 120, borderRadius: 8, display: "block", margin: "4px auto 0" }} onError={() => {}} /> : "Image"}
                </div>
              )}
              {form.headerType === "VIDEO" && (
                <div className="tpl-media-placeholder" style={{ borderRadius: 8, marginBottom: 8, background: "#e2e8f0", padding: "24px 0", textAlign: "center", fontSize: 28 }}>🎥 Video</div>
              )}
              {form.headerType === "DOCUMENT" && (
                <div className="tpl-media-placeholder" style={{ borderRadius: 8, marginBottom: 8, background: "#e2e8f0", padding: "24px 0", textAlign: "center", fontSize: 28 }}>📄 Document</div>
              )}
              <p className="ct-preview-body">{previewBody || "Your message will appear here..."}</p>
              {form.footer && <p className="ct-preview-footer">{form.footer}</p>}
              <span className="ct-preview-time">10:30 AM</span>
            </div>
            {form.buttons.map((btn, i) => (
              <div key={i} className="ct-preview-btn">{btn.text || "Button"}</div>
            ))}
          </div>
          <div className="ct-category-badge">{form.category}</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CAMPAIGNS VIEW — main entry point
// ═══════════════════════════════════════════════════════════════
const CAMPAIGN_CATEGORIES = ["Affiliate", "Corporate", "Individual"];

function Pagination({ label, page = 1, limit = 25, total, totalPages = 1, hasNext, loading = false, onPage, onLimit }) {
  const next = hasNext ?? page < totalPages;
  return <nav className="list-pagination" aria-label={`${label} pagination`}>
    <span>{total == null ? label : total === 0 ? "0 results" : `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`}</span>
    <div className="list-pagination-controls">
      <label>Rows per page <select aria-label={`${label} rows per page`} value={limit} disabled={loading} onChange={e => onLimit(Number(e.target.value))}>
        {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
      </select></label>
      <button className="btn-sm" disabled={loading || page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span>Page {page}{total == null ? "" : ` of ${totalPages}`}</span>
      <button className="btn-sm" disabled={loading || !next} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  </nav>;
}

function useCampaignData(campaignId, path, key) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const requestId = useRef(0);
  useEffect(() => {
    requestId.current++;
    setItems(null);
    setLoading(false);
    setError("");
    setPagination({ page: 1, limit: 25, total: 0, totalPages: 1 });
    return () => { requestId.current++; };
  }, [campaignId, path]);
  const load = async (page = pagination.page, limit = pagination.limit) => {
    const request = ++requestId.current;
    setItems(previous => previous || []);
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/campaigns/${campaignId}/${path}${path.includes("?") ? "&" : "?"}page=${page}&limit=${limit}`);
      if (request !== requestId.current) return;
      if (data.error) throw new Error(data.error);
      setItems(data[key] || []);
      setPagination({ page: data.page || 1, limit: data.limit || limit, total: data.total || 0, totalPages: data.totalPages || 1 });
    } catch (error) { if (request === requestId.current) setError(error.message || "Failed to load contacts"); }
    finally { if (request === requestId.current) setLoading(false); }
  };
  const close = () => { requestId.current++; setItems(null); setLoading(false); };
  return { items, loading, error, pagination, load, close };
}

// ═══════════════════════════════════════════════════════════════
// HUBSPOT WORKFLOW VIEW — internal users + action card setup
// ═══════════════════════════════════════════════════════════════
const HS_WF_BASE = "https://whatsapp.myforexeye.com/api/hubspot/workflow";

function HubSpotWorkflowView() {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [owners, setOwners]       = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [editUser, setEditUser]   = useState(null); // null = new, else existing user object
  const [form, setForm]           = useState({ name: "", phone: "", hubspotOwnerId: "" });
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState({ text: "", ok: true });

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: "", ok: true }), 3500); };

  const fetchUsers = async () => {
    setLoading(true);
    const data = await apiFetch("/internal-users");
    setUsers(data.users || []);
    setLoading(false);
  };

  const fetchOwners = async () => {
    setOwnersLoading(true);
    const data = await apiFetch("/hubspot/owners");
    setOwners(data.owners || []);
    setOwnersLoading(false);
  };

  useEffect(() => { fetchUsers(); fetchOwners(); }, []);

  const openAdd = () => {
    setEditUser(null);
    setForm({ name: "", phone: "", hubspotOwnerId: "" });
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ name: user.name, phone: user.phone, hubspotOwnerId: user.hubspotOwnerId || "" });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) { flash("Name and phone are required", false); return; }
    setSaving(true);
    const method = editUser ? "PUT" : "POST";
    const url = editUser ? `/internal-users/${editUser.id}` : "/internal-users";
    const data = await apiFetch(url, { method, body: JSON.stringify(form) });
    setSaving(false);
    if (data.success) {
      flash(editUser ? "User updated." : "User added.");
      setShowForm(false);
      fetchUsers();
    } else {
      flash(data.error || "Failed to save", false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Remove "${user.name}" from internal users?`)) return;
    const data = await apiFetch(`/internal-users/${user.id}`, { method: "DELETE" });
    if (data.success) { flash("Removed."); fetchUsers(); }
    else flash(data.error || "Failed to remove", false);
  };

  const ownerLabel = (ownerId) => {
    if (!ownerId) return null;
    const o = owners.find(o => o.id === ownerId);
    return o ? `${o.label} (${o.email})` : ownerId;
  };

  return (
    <div style={{ padding: "0 24px 32px", maxWidth: 900, margin: "0 auto" }}>

      {/* ── Action Cards Setup ── */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>HubSpot Custom Action Cards</h3>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
          Add these two custom action cards in <strong>HubSpot → Settings → Integrations → Private Apps → Custom Actions</strong>.
          Each card requires your <code>HUBSPOT_WORKFLOW_CLIENT_SECRET</code> for signature verification.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

          {/* Card 1: Send Campaign to Contact */}
          <div style={{ border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "var(--bg-sidebar)", borderBottom: "1px solid var(--border-color)", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>👤</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Send Campaign to Contact</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Sends to the enrolled contact's phone</div>
              </div>
            </div>
            <div style={{ padding: "12px 16px" }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>FETCH OPTIONS</label>
                <code style={{ fontSize: 11, color: "#8b5cf6", wordBreak: "break-all" }}>{HS_WF_BASE}/campaigns</code>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>EXECUTE</label>
                <code style={{ fontSize: 11, color: "#8b5cf6", wordBreak: "break-all" }}>{HS_WF_BASE}/send-campaign</code>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-secondary)" }}>
                Input fields: <strong>recipientPhone</strong>, <strong>campaignId</strong>, <strong>variable1</strong> (optional)
              </div>
            </div>
          </div>

          {/* Card 2: Send Internal Notification */}
          <div style={{ border: "1px solid #8b5cf640", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "rgba(139,92,246,.08)", borderBottom: "1px solid #8b5cf640", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🔔</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Send Internal Notification</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Sends to an internal team member</div>
              </div>
            </div>
            <div style={{ padding: "12px 16px" }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>FETCH OPTIONS (recipient picker)</label>
                <code style={{ fontSize: 11, color: "#8b5cf6", wordBreak: "break-all" }}>{HS_WF_BASE}/notify-options</code>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>CAMPAIGNS FETCH OPTIONS</label>
                <code style={{ fontSize: 11, color: "#8b5cf6", wordBreak: "break-all" }}>{HS_WF_BASE}/campaigns</code>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>EXECUTE</label>
                <code style={{ fontSize: 11, color: "#8b5cf6", wordBreak: "break-all" }}>{HS_WF_BASE}/send-notification</code>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-secondary)" }}>
                Input fields: <strong>recipientId</strong> (dropdown), <strong>customPhone</strong> (when custom), <strong>campaignId</strong>, <strong>variable1</strong> (optional)
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Internal Users Table ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)", marginBottom: 2 }}>Internal Users</h3>
          <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            Team members who appear in the "Send Internal Notification" dropdown. Each needs a WhatsApp phone number.
          </p>
        </div>
        <button className="btn-primary" style={{ padding: "8px 16px", fontSize: 13, whiteSpace: "nowrap" }} onClick={openAdd}>
          + Add User
        </button>
      </div>

      {msg.text && <div className={`wap-alert ${msg.ok ? "ok" : "err"}`} style={{ marginBottom: 12 }}>{msg.text}</div>}

      {loading ? (
        <div className="template-loading">Loading internal users…</div>
      ) : users.length === 0 ? (
        <div style={{ padding: "24px", textAlign: "center", border: "1px dashed var(--border-color)", borderRadius: 10, color: "var(--text-muted)", fontSize: 13 }}>
          No internal users yet. Add team members who should receive WhatsApp notifications from HubSpot workflows.
        </div>
      ) : (
        <div className="am-table-wrap">
          <table className="tpl-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>WhatsApp Phone</th>
                <th>HubSpot Owner</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="tpl-row" style={{ cursor: "default" }}>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td><code style={{ fontSize: 12 }}>+{u.phone}</code></td>
                  <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    {u.hubspotOwnerId ? (ownerLabel(u.hubspotOwnerId) || u.hubspotOwnerId) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-sm" onClick={() => openEdit(u)}>Edit</button>
                      <button className="btn-sm" style={{ color: "var(--danger)" }} onClick={() => handleDelete(u)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editUser ? "Edit Internal User" : "Add Internal User"}</h3>
              <button className="btn-icon" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <div className="input-group">
                <label>Name <span className="ct-req">*</span></label>
                <input placeholder="e.g. Sales Team Lead" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div className="input-group">
                <label>WhatsApp Phone <span className="ct-req">*</span></label>
                <input placeholder="e.g. 919876543210 (country code, no +)" value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <span className="ct-hint-text">Include country code without the + sign</span>
              </div>
              <div className="input-group">
                <label>Link to HubSpot Owner <span className="ct-opt">Optional</span></label>
                {ownersLoading ? (
                  <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0" }}>Loading HubSpot owners…</div>
                ) : (
                  <select value={form.hubspotOwnerId} onChange={e => setForm(f => ({ ...f, hubspotOwnerId: e.target.value }))}>
                    <option value="">— Not linked —</option>
                    {owners.map(o => (
                      <option key={o.id} value={o.id}>{o.label}{o.email ? ` (${o.email})` : ""}</option>
                    ))}
                  </select>
                )}
                <span className="ct-hint-text">Links this user to a HubSpot owner for reference only — phone above is used for sending</span>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleSave} disabled={saving || !form.name.trim() || !form.phone.trim()}>
                {saving ? "Saving…" : editUser ? "Update" : "Add User"}
              </button>
              <button className="btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignsView() {
  const [section, setSection]          = useState("campaigns"); // "campaigns" | "hubspot"
  const [view, setView]               = useState("list"); // "list" | "create" | "detail"
  const [campaigns, setCampaigns]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selectedId, setSelectedId]   = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listLimit, setListLimit] = useState(25);
  const [listPagination, setListPagination] = useState({ total: 0, totalPages: 1 });
  const listRequest = useRef(0);
  const [msg, setMsg]                 = useState({ text: "", ok: true });

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: "", ok: true }), 4000); };

  const fetchCampaigns = useCallback(async () => {
    const request = ++listRequest.current;
    setLoading(true);
    try {
      const data = await apiFetch(`/campaigns?archived=${showArchived}&page=${listPage}&limit=${listLimit}`);
      if (request === listRequest.current) {
        setCampaigns(data.campaigns || []);
        setListPagination({ total: data.total || 0, totalPages: data.totalPages || 1 });
        if (data.page) setListPage(data.page);
      }
    } finally { if (request === listRequest.current) setLoading(false); }
  }, [showArchived, listPage, listLimit]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const hasSendingCampaign = campaigns.some(c => c.status === "sending");
  useEffect(() => {
    // Fast poll while sending; slow poll when sent to pick up read receipts & link clicks
    const t = setInterval(fetchCampaigns, hasSendingCampaign ? 8000 : 30000);
    return () => clearInterval(t);
  }, [hasSendingCampaign, fetchCampaigns]);

  const openEdit = async (id) => {
    setActionBusy(true);
    try {
      const [data, templates] = await Promise.all([apiFetch(`/campaigns/${id}`), apiFetch("/templates")]);
      if (!data.campaign) throw new Error(data.error || "Campaign not found");
      const definition = (templates.templates || []).find(t => t.name === data.campaign.template?.name && t.language === (data.campaign.template?.language || "en"));
      if (!definition) throw new Error("Unable to load this campaign's template. Please refresh and try again.");
      setEditingCampaign({ ...data.campaign, templateDefinition: definition });
      setSelectedId(id);
      setView("edit");
    } catch (error) { flash(error.message, false); }
    finally { setActionBusy(false); }
  };

  const archiveCampaign = async (campaign) => {
    if (!window.confirm(`Archive "${campaign.name}"? Future sends will stop. A message already being sent may finish. You can restore it from Archived campaigns. Sent messages and existing tracking links will remain available.`)) return;
    setActionBusy(true);
    try {
      const data = await apiFetch(`/campaigns/${campaign.id}`, { method: "DELETE" });
      if (!data.success) throw new Error(data.error || "Failed to archive campaign");
      setView("list");
      setSelectedId(null);
      await fetchCampaigns();
      flash("Campaign archived");
    } catch (error) { flash(error.message, false); }
    finally { setActionBusy(false); }
  };

  const restoreCampaign = async (campaign) => {
    setActionBusy(true);
    try {
      const data = await apiFetch(`/campaigns/${campaign.id}/restore`, { method: "POST" });
      if (!data.success) throw new Error(data.error || "Failed to restore campaign");
      await fetchCampaigns();
      flash("Campaign restored. Find it under Active campaigns.");
    } catch (error) { flash(error.message, false); }
    finally { setActionBusy(false); }
  };

  const resumeCampaign = async (campaign) => {
    if (!window.confirm(`Resume "${campaign.name}" and send to its remaining contacts now?`)) return;
    setActionBusy(true);
    try {
      const data = await apiFetch(`/campaigns/${campaign.id}/resume`, { method: "POST" });
      if (!data.success) throw new Error(data.error || "Failed to resume campaign");
      await fetchCampaigns();
      flash("Campaign resumed");
    } catch (error) { flash(error.message, false); }
    finally { setActionBusy(false); }
  };

  if (section === "hubspot") {
    return (
      <div className="camp-root">
        <div className="camp-header" style={{ marginBottom: 8 }}>
          <div>
            <h2>Campaigns</h2>
            <p className="wap-subtitle">HubSpot workflow action cards and internal notification settings</p>
          </div>
        </div>
        <div className="camp-section-tabs">
          <button className={`camp-section-tab${section === "campaigns" ? " active" : ""}`} onClick={() => setSection("campaigns")}>
            <Icons.Campaign /> Campaigns
          </button>
          <button className={`camp-section-tab${section === "hubspot" ? " active" : ""}`} onClick={() => setSection("hubspot")}>
            <Icons.HubSpot /> HubSpot Workflow
          </button>
        </div>
        <HubSpotWorkflowView />
      </div>
    );
  }

  if (view === "edit" && editingCampaign) {
    return <CreateCampaignForm key={editingCampaign.id} initialCampaign={editingCampaign}
      onBack={() => setView("detail")}
      onCreated={() => { setView("detail"); fetchCampaigns(); flash("Campaign updated"); }} />;
  }

  if (view === "create") {
    return (
      <CreateCampaignForm
        onBack={() => setView("list")}
        onCreated={(campaign) => { fetchCampaigns(); setSelectedId(campaign.id); setView("detail"); flash("Campaign created!"); }}
      />
    );
  }

  if (view === "detail" && selectedId) {
    return (
      <CampaignDetail
        onEdit={() => openEdit(selectedId)} onArchive={archiveCampaign} actionBusy={actionBusy} notice={msg}
        campaignId={selectedId}
        onBack={() => { setView("list"); fetchCampaigns(); }}
        onFlash={flash}
      />
    );
  }

  const STATUS_MAP = {
    paused:     { label: "Paused", cls: "camp-s-pending" },
    pending:    { label: "Pending",      cls: "camp-s-pending"   },
    scheduled:  { label: "Scheduled",    cls: "camp-s-scheduled" },
    sending:    { label: "Sending…",     cls: "camp-s-sending"   },
    sent:       { label: "Sent",         cls: "camp-s-sent"      },
    failed:     { label: "Failed",       cls: "camp-s-failed"    },
    api_active: { label: "API Active",   cls: "camp-s-api"       },
  };

  return (
    <div className="camp-root">
      <div className="camp-header" style={{ marginBottom: 8 }}>
        <div>
          <h2>Campaigns</h2>
          <p className="wap-subtitle">Send WhatsApp template messages to HubSpot contact lists</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-sm" onClick={fetchCampaigns}><Icons.Refresh /> Refresh</button>
          <button className="btn-primary" style={{ padding: "8px 18px", fontSize: 13 }} onClick={() => setView("create")}>
            + New Campaign
          </button>
        </div>
      </div>

      <div className="camp-section-tabs">
        <button className={`camp-section-tab${section === "campaigns" ? " active" : ""}`} onClick={() => setSection("campaigns")}>
          <Icons.Campaign /> Campaigns
        </button>
        <button className={`camp-section-tab${section === "hubspot" ? " active" : ""}`} onClick={() => setSection("hubspot")}>
          <Icons.HubSpot /> HubSpot Workflow
        </button>
      </div>

      <div role="group" aria-label="Campaign visibility" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className="btn-sm" aria-pressed={!showArchived} disabled={actionBusy} onClick={() => { setListPage(1); setShowArchived(false); }}>Active campaigns</button>
        <button className="btn-sm" aria-pressed={showArchived} disabled={actionBusy} onClick={() => { setListPage(1); setShowArchived(true); }}>Archived campaigns</button>
      </div>
      {showArchived && <p className="wap-subtitle" style={{ marginBottom: 16 }}>Restore a campaign to return it to Active campaigns. List campaigns that were waiting or running return paused until you choose Resume.</p>}

      {msg.text && <div className={`wap-alert ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>}

      {loading ? (
        <div className="template-loading">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="camp-empty">
          <div className="camp-empty-icon">📢</div>
          <h3>{showArchived ? "No archived campaigns" : "No campaigns yet"}</h3>
          <p>{showArchived ? "Campaigns you archive will appear here and can be restored." : "Create your first WhatsApp campaign to reach contacts in your HubSpot lists."}</p>
          <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => setView("create")}>Create Campaign</button>
        </div>
      ) : (
        <div className="camp-table-wrap">
          <table className="tpl-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Category</th>
                <th>Template</th>
                <th>HubSpot Lists</th>
                <th>Status</th>
                <th>Total</th>
                <th>Delivered</th>
                <th>Read</th>
                <th>Failed</th>
                <th>Delivery %</th>
                <th>Created</th>
                {showArchived && <th>Archived</th>}
                {can(getUser(), "send_campaign") && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => {
                const s = c.stats || {};
                const badge = c.deletedAt ? { label: "Archived", cls: "camp-s-pending" } : STATUS_MAP[c.status] || { label: c.status, cls: "" };
                return (
                  <tr key={c.id} className="tpl-row"
                    onClick={() => { if (!showArchived) { setSelectedId(c.id); setView("detail"); } }}>
                    <td>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      <span className="am-agent-username" style={{ display: "block" }}>by {c.createdBy}</span>
                    </td>
                    <td>{c.category || "Not set"}</td>
                    <td>
                      <code style={{ fontSize: 12 }}>{c.template?.name}</code>
                      {c.type === "api" && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: "#8b5cf620", color: "#8b5cf6", padding: "1px 6px", borderRadius: 10, fontWeight: 700 }}>API</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-secondary)", maxWidth: 180 }}>
                      {c.type === "api"
                        ? <span style={{ color: "#8b5cf6", fontWeight: 500 }}>API Triggered</span>
                        : (c.hubspotLists || []).map(l => l.listName).join(", ")}
                    </td>
                    <td><span className={`camp-status-badge ${badge.cls}`}>{badge.label}</span></td>
                    <td style={{ textAlign: "center" }}>{s.total || 0}</td>
                    <td style={{ textAlign: "center", color: "var(--green-primary)" }}>
                      {(s.delivered || 0) + (s.read || 0)}
                    </td>
                    <td style={{ textAlign: "center", color: "#53bdeb" }}>{s.read || 0}</td>
                    <td style={{ textAlign: "center", color: "var(--danger)" }}>{s.failed || 0}</td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{ color: (s.deliveryRate || 0) >= 70 ? "var(--green-primary)" : "var(--orange)" }}>
                        {s.deliveryRate || 0}%
                      </span>
                    </td>
                    <td style={{ color: "var(--text-secondary)", fontSize: 12, whiteSpace: "nowrap" }}>
                      {new Date(c.createdAt).toLocaleDateString()}
                    </td>
                    {showArchived && <td>{c.deletedAt ? new Date(c.deletedAt).toLocaleString() : "—"}</td>}
                    {can(getUser(), "send_campaign") && <td>
                      <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
                        {showArchived ? <button className="btn-sm" disabled={actionBusy} onClick={() => restoreCampaign(c)}>Restore</button> : <>
                        {c.status === "paused" && <button className="btn-sm" disabled={actionBusy} onClick={() => resumeCampaign(c)}>Resume</button>}
                        <button className="btn-sm" disabled={actionBusy || c.status === "sending"} onClick={() => openEdit(c.id)}>Edit</button>
                        <button className="btn-sm" style={{ color: "var(--danger)" }} disabled={actionBusy} onClick={() => archiveCampaign(c)}>Archive</button>
                        </>}
                      </div>
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pagination label="Campaigns" page={listPage} limit={listLimit} {...listPagination} loading={loading || actionBusy}
        onPage={setListPage} onLimit={limit => { setListLimit(limit); setListPage(1); }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CREATE CAMPAIGN FORM
// ═══════════════════════════════════════════════════════════════
function CreateCampaignForm({ onBack, onCreated, initialCampaign = null }) {
  const isEditing = !!initialCampaign;
  const templateLocked = isEditing && (initialCampaign.stats?.total || 0) > 0;
  const canReschedule = isEditing && !initialCampaign.sentAt && ["pending", "scheduled"].includes(initialCampaign.status);
  const localDateTime = value => { if (!value) return ""; const date = new Date(value); return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
  const [form, setForm] = useState({
    name: "", category: "", campaignType: "hubspot", lists: [], template: null,
    variableMapping: {}, scheduleType: "now", scheduledAt: "",
    headerMediaUrl: "",
    batchEnabled: false, batchSize: 1000, batchDelayMinutes: 60,
    ...(initialCampaign ? {
      name: initialCampaign.name, category: initialCampaign.category || "", campaignType: initialCampaign.type || "hubspot",
      lists: initialCampaign.hubspotLists || [], template: initialCampaign.templateDefinition,
      variableMapping: initialCampaign.variableMapping || {}, headerMediaUrl: initialCampaign.headerMediaUrl || "",
      scheduleType: initialCampaign.scheduledAt ? "schedule" : "now", scheduledAt: localDateTime(initialCampaign.scheduledAt),
      batchEnabled: initialCampaign.batchSize > 0, batchSize: initialCampaign.batchSize || 1000, batchDelayMinutes: initialCampaign.batchDelayMinutes || 60,
    } : {}),
  });
  const [hsLists, setHsLists]         = useState([]);
  const [hsLoading, setHsLoading]     = useState(true);
  const [listPickerOpen, setListPickerOpen] = useState(true);
  const [templates, setTemplates]     = useState([]);
  const [tplLoaded, setTplLoaded]     = useState(false);
  const [tplLoading, setTplLoading]   = useState(false);
  const [tplPickerOpen, setTplPickerOpen]   = useState(true);
  const [preview, setPreview]         = useState(null);
  const [prevLoading, setPrevLoading] = useState(false);
  const [listSearch, setListSearch]   = useState("");
  const [tplSearch, setTplSearch]     = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState("");
  const [mediaSize, setMediaSize]     = useState({ loading: false, bytes: null, fetchError: false });
  const [urlButtonTargets, setUrlButtonTargets] = useState(() => {
    const targets = Object.fromEntries((initialCampaign?.urlButtonConfigs || []).filter(c => c.mode === "tracking").map(c => [c.index, c.value]));
    if (!Object.keys(targets).length && initialCampaign?.trackingTargetUrl) {
      (initialCampaign.templateDefinition?.components?.find(c => c.type === "BUTTONS")?.buttons || []).forEach((button, index) => {
        if (button.type === "URL" && button.url?.includes("{{1}}")) targets[index] = initialCampaign.trackingTargetUrl;
      });
    }
    return targets;
  }); // buttonIndex → destination URL

  // HubSpot properties per object type (loaded lazily)
  const [hsProps, setHsProps]             = useState({ contact: null, deal: null, company: null });
  const [hsPropLoading, setHsPropLoading] = useState({ contact: false, deal: false, company: false });
  const hsPropRequested = useRef(new Set());

  // Tracks which object type each variable has selected — separate from the actual mapping value
  // so selecting "Contact" without a property yet doesn't immediately revert to "Static Text"
  const [varObjTypes, setVarObjTypes] = useState({});

  const loadHsProps = useCallback(async (objType) => {
    if (hsPropRequested.current.has(objType)) return;
    hsPropRequested.current.add(objType);
    const plural = { contact: "contacts", deal: "deals", company: "companies" }[objType];
    if (!plural) return;
    setHsPropLoading(s => ({ ...s, [objType]: true }));
    try {
      const data = await apiFetch(`/hubspot/properties/${plural}`);
      setHsProps(s => ({ ...s, [objType]: data.properties || [] }));
    } catch {
      setHsProps(s => ({ ...s, [objType]: [] }));
      hsPropRequested.current.delete(objType); // allow retry on error
    } finally {
      setHsPropLoading(s => ({ ...s, [objType]: false }));
    }
  }, []);

  useEffect(() => {
    apiFetch("/hubspot/lists").then(data => {
      setHsLists(data.lists || []);
      setHsLoading(false);
    });
  }, []);

  // Pre-load all object properties and initialise varObjTypes when a template is selected
  useEffect(() => {
    if (!form.template) return;
    const vars = getTemplateVars(form.template);
    if (!vars.length) return;

    // Seed varObjTypes from any existing mapping values (e.g. when retaining state)
    const seed = {};
    vars.forEach(v => {
      const m = form.variableMapping[v] || "";
      if (m.startsWith("@contact.") || m === "@firstName" || m === "@lastName" || m === "@fullName" || m === "@phone")
        seed[v] = "contact";
      else if (m.startsWith("@deal."))    seed[v] = "deal";
      else if (m.startsWith("@company.")) seed[v] = "company";
    });
    setVarObjTypes(seed);

    loadHsProps("contact");
    loadHsProps("deal");
    loadHsProps("company");
  }, [form.template?.name, loadHsProps]);

  // Debounced media size check whenever the header URL changes
  useEffect(() => {
    const url = form.headerMediaUrl?.trim();
    const hc = form.template?.components?.find(c => c.type === "HEADER");
    if (!url || !["VIDEO", "IMAGE", "DOCUMENT"].includes(hc?.format)) {
      setMediaSize({ loading: false, bytes: null, fetchError: false });
      return;
    }
    setMediaSize({ loading: true, bytes: null, fetchError: false });
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetch(`/check-media-size?url=${encodeURIComponent(url)}`);
        setMediaSize({ loading: false, bytes: data.sizeBytes ?? null, fetchError: false });
      } catch {
        setMediaSize({ loading: false, bytes: null, fetchError: true });
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [form.headerMediaUrl, form.template]);

  const refreshLists = async () => {
    setHsLoading(true);
    const data = await apiFetch("/hubspot/lists?refresh=true");
    setHsLists(data.lists || []);
    setHsLoading(false);
  };

  const loadTemplates = async () => {
    setTplLoading(true);
    const data = await apiFetch("/templates");
    setTemplates((data.templates || []).filter(t => t.status === "APPROVED"));
    setTplLoaded(true);
    setTplLoading(false);
  };

  const previewContacts = async () => {
    if (!form.lists.length) return;
    setPrevLoading(true);
    let total = 0, valid = 0, invalid = 0;
    for (const list of form.lists) {
      const data = await apiFetch(`/hubspot/lists/${list.listId}/contacts`);
      total   += data.total        || 0;
      valid   += data.validCount   || 0;
      invalid += data.invalidCount || 0;
    }
    setPreview({ total, validCount: valid, invalidCount: invalid });
    setPrevLoading(false);
  };

  const toggleList = (list) => {
    const exists = form.lists.find(l => l.listId === list.listId);
    setForm(f => ({
      ...f,
      lists: exists
        ? f.lists.filter(l => l.listId !== list.listId)
        : [...f.lists, { listId: list.listId, listName: list.listName, contactCount: list.contactCount }],
    }));
    setPreview(null);
  };

  const getTemplateVars = (tpl) => {
    if (!tpl) return [];
    const vars = [];
    (tpl.components || []).forEach(c => {
      const matches = (c.text || "").match(/{{\d+}}/g) || [];
      matches.forEach(m => { const n = m.replace(/[{}]/g, ""); if (!vars.includes(n)) vars.push(n); });
    });
    return vars;
  };

  const handleSubmit = async () => {
    setError("");
    const isApi = form.campaignType === "api";
    if (!form.name.trim())                                     return setError("Campaign name is required");
    if (!isApi && !form.lists.length)                          return setError("Select at least one HubSpot list");
    if (!form.template)                                        return setError("Select a WhatsApp template");
    if ((!isEditing || canReschedule) && !isApi && form.scheduleType === "schedule" && !form.scheduledAt) return setError("Please select a send date/time");
    const submitHdrComp = form.template?.components?.find(c => c.type === "HEADER");
    const submitHdrMediaFormat = submitHdrComp && ["VIDEO", "IMAGE", "DOCUMENT"].includes(submitHdrComp.format) ? submitHdrComp.format : null;
    if (submitHdrMediaFormat && !form.headerMediaUrl.trim()) return setError(`A ${submitHdrMediaFormat.toLowerCase()} URL is required for this template's header`);
    const mediaSizeLimits = { VIDEO: 16, IMAGE: 5, DOCUMENT: 100 };
    if (submitHdrMediaFormat && mediaSize.bytes !== null) {
      const limitMB = mediaSizeLimits[submitHdrMediaFormat] || 16;
      if (mediaSize.bytes / (1024 * 1024) > limitMB) return setError(`${submitHdrMediaFormat.charAt(0) + submitHdrMediaFormat.slice(1).toLowerCase()} size exceeds the ${limitMB} MB WhatsApp limit`);
    }

    setSubmitting(true);
    const urlButtonConfigs = urlTrackingButtons
      .filter(btn => urlButtonTargets[btn.buttonIndex]?.trim())
      .map(btn => ({ index: btn.buttonIndex, mode: "tracking", value: urlButtonTargets[btn.buttonIndex].trim() }));

    const apiVars = isApi ? getTemplateVars(form.template) : [];

    try {
      const data = await apiFetch(isEditing ? `/campaigns/${initialCampaign.id}` : "/campaigns", {
        method: isEditing ? "PATCH" : "POST",
        body: JSON.stringify({
          type:               isApi ? "api" : "hubspot",
          name:               form.name.trim(),
          category:           form.category,
          hubspotLists:       isApi ? [] : form.lists,
          template:           { name: form.template.name, language: form.template.language || "en" },
          variableMapping:    isApi ? {} : form.variableMapping,
          ...((!isEditing || canReschedule) && !isApi ? { scheduledAt: form.scheduleType === "schedule" ? new Date(form.scheduledAt).toISOString() : null } : {}),
          headerMediaUrl:     form.headerMediaUrl.trim() || null,
          headerMediaType:    submitHdrMediaFormat,
          batchSize:          (!isApi && form.batchEnabled) ? parseInt(form.batchSize) || 1000 : 0,
          batchDelayMinutes:  (!isApi && form.batchEnabled) ? parseInt(form.batchDelayMinutes) || 60 : 0,
          urlButtonConfigs:   [...urlButtonConfigs, ...(isEditing && form.template.name === initialCampaign.template.name ? (initialCampaign.urlButtonConfigs || []).filter(c => c.mode !== "tracking" && !urlButtonConfigs.some(b => b.index === c.index)) : [])],
          ...(isEditing ? { trackingTargetUrl: null } : {}),
          apiVars,
        }),
      });
      if (data.success) onCreated(data.campaign);
      else setError(data.error || "Failed to save campaign");
    } catch (error) { setError(error.message || "Failed to save campaign"); }
    finally { setSubmitting(false); }
  };

  const filteredLists = hsLists.filter(l =>
    l.listName.toLowerCase().includes(listSearch.toLowerCase())
  );
  const filteredTpls = templates.filter(t =>
    t.name.toLowerCase().includes(tplSearch.toLowerCase())
  );
  const templateVars = getTemplateVars(form.template);

  const bodyComp = form.template?.components?.find(c => c.type === "BODY");
  const hdrComp  = form.template?.components?.find(c => c.type === "HEADER");
  const ftrComp  = form.template?.components?.find(c => c.type === "FOOTER");
  const hdrMediaFormat = hdrComp && ["VIDEO", "IMAGE", "DOCUMENT"].includes(hdrComp.format) ? hdrComp.format : null;
  const urlTrackingButtons = (form.template?.components?.find(c => c.type === "BUTTONS")?.buttons || [])
    .map((btn, idx) => ({ ...btn, buttonIndex: idx }))
    .filter(btn => btn.type === "URL" && btn.url?.includes("{{1}}"));

  return (
    <div className="camp-create-root">
      <div className="camp-create-header">
        <button className="btn-icon" onClick={onBack}><Icons.ArrowLeft /></button>
        <div>
          <h2>{isEditing ? "Edit Campaign" : "New Campaign"}</h2>
          <p className="wap-subtitle">{isEditing ? "Update campaign settings for future sends. Sent messages keep their original content and links." : "Send a WhatsApp template to HubSpot contacts"}</p>
        </div>
      </div>

      <div className="camp-create-body">
        <div className="camp-create-form">
          {error && <div className="error-msg">{error}</div>}

          {/* Campaign name */}
          <div className="ct-section">
            <h3 className="ct-section-title">Campaign Details</h3>
            <div className="input-group">
              <label>Campaign Name <span className="ct-req">*</span></label>
              <input
                placeholder="e.g. June Promo 2026"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="input-group">
              <label htmlFor="campaign-category">Category</label>
              <select id="campaign-category" value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option value="">Not set</option>
                {CAMPAIGN_CATEGORIES.map(category => <option key={category} value={category}>{category}</option>)}
              </select>
            </div>
          </div>

          {/* Campaign type toggle */}
          <div className="ct-section">
            <h3 className="ct-section-title">Campaign Type</h3>
            <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid var(--border-color)", width: "fit-content" }}>
              {[{ value: "hubspot", label: "HubSpot List" }, { value: "api", label: "API Triggered" }].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={isEditing}
                  onClick={() => setForm(f => ({ ...f, campaignType: opt.value }))}
                  style={{
                    padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none",
                    background: form.campaignType === opt.value ? "var(--brand-primary, #6366f1)" : "var(--bg-sidebar)",
                    color:      form.campaignType === opt.value ? "#fff" : "var(--text-secondary)",
                    transition: "background 0.15s",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {form.campaignType === "api" && (
              <p style={{ marginTop: 10, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>
                No contact list needed. Send via the API from n8n or any system — each call sends to one contact.
                Full delivery, read, and click tracking is recorded per message.
              </p>
            )}
          </div>

          {/* HubSpot list picker — hidden for API campaigns */}
          {form.campaignType !== "api" && <div className="ct-section">
            <div className="camp-section-toolbar" style={{ cursor: "pointer" }} onClick={() => setListPickerOpen(o => !o)}>
              <h3 className="ct-section-title" style={{ margin: 0 }}>
                HubSpot Contact Lists <span className="ct-req">*</span>
                {form.lists.length > 0 && (
                  <span style={{ fontWeight: 400, fontSize: 12, color: "var(--green-primary)", marginLeft: 8 }}>
                    {form.lists.length} selected
                  </span>
                )}
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {listPickerOpen && (
                  <button className="btn-sm" onClick={e => { e.stopPropagation(); refreshLists(); }} disabled={hsLoading}>
                    <Icons.Refresh /> Refresh
                  </button>
                )}
                <Icons.Chevron open={listPickerOpen} />
              </div>
            </div>

            {!listPickerOpen && form.lists.length > 0 && (
              <div className="camp-selected-lists" style={{ marginTop: 10 }}>
                {form.lists.map(l => (
                  <span key={l.listId} className="camp-list-tag">
                    {l.listName}
                    <span className="camp-tag-count">({l.contactCount})</span>
                    <button className="camp-tag-remove" onClick={e => { e.stopPropagation(); toggleList(l); }}>×</button>
                  </span>
                ))}
              </div>
            )}

            {listPickerOpen && (
              <>
                {form.lists.length > 0 && (
                  <div className="camp-selected-lists" style={{ marginTop: 10 }}>
                    {form.lists.map(l => (
                      <span key={l.listId} className="camp-list-tag">
                        {l.listName}
                        <span className="camp-tag-count">({l.contactCount})</span>
                        <button className="camp-tag-remove" onClick={() => toggleList(l)}>×</button>
                      </span>
                    ))}
                  </div>
                )}

                <input
                  className="camp-search-input"
                  placeholder="Search lists…"
                  value={listSearch}
                  onChange={e => setListSearch(e.target.value)}
                  style={{ marginTop: 10 }}
                />

                {hsLoading ? (
                  <div className="template-loading" style={{ padding: "10px 0" }}>Loading HubSpot lists…</div>
                ) : (
                  <div className="camp-list-picker">
                    {filteredLists.map(l => {
                      const sel = form.lists.some(sl => sl.listId === l.listId);
                      return (
                        <div key={l.listId} className={`camp-list-item ${sel ? "selected" : ""}`} onClick={() => toggleList(l)}>
                          <div style={{ flex: 1 }}>
                            <span className="camp-list-name">{l.listName}</span>
                            <span className="camp-list-meta">{l.listType} · {l.contactCount.toLocaleString()} contacts</span>
                          </div>
                          {sel && <span style={{ color: "var(--green-primary)", fontSize: 16 }}>✓</span>}
                        </div>
                      );
                    })}
                    {filteredLists.length === 0 && (
                      <div style={{ padding: 12, color: "var(--text-muted)", fontSize: 13 }}>
                        {hsLists.length === 0 ? "No HubSpot lists found." : "No lists match your search."}
                      </div>
                    )}
                  </div>
                )}

                {form.lists.length > 0 && (
                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
                    <button className="btn-sm" onClick={previewContacts} disabled={prevLoading}>
                      {prevLoading ? "Loading…" : "Preview Contacts"}
                    </button>
                    {preview && (
                      <div className="camp-preview-inline">
                        <span className="camp-preview-stat">{preview.total} total</span>
                        <span className="camp-preview-stat ok">{preview.validCount} valid</span>
                        <span className="camp-preview-stat err">{preview.invalidCount} invalid</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>}

          {/* Template picker */}
          <div className="ct-section">
            <div className="camp-section-toolbar" style={{ cursor: "pointer" }} onClick={() => { if (!templateLocked) setTplPickerOpen(o => !o); }}>
              <h3 className="ct-section-title" style={{ margin: 0 }}>
                WhatsApp Template <span className="ct-req">*</span>
                {form.template && (
                  <span style={{ fontWeight: 400, fontSize: 12, color: "var(--green-primary)", marginLeft: 8 }}>
                    {form.template.name}
                  </span>
                )}
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {tplPickerOpen && !tplLoaded && (
                  <button className="btn-sm" onClick={e => { e.stopPropagation(); loadTemplates(); }} disabled={tplLoading}>
                    {tplLoading ? "Loading…" : "Load Templates"}
                  </button>
                )}
                <Icons.Chevron open={tplPickerOpen} />
              </div>
            </div>

            {templateLocked && <p className="wap-subtitle">The template is fixed after sending starts. Create a new campaign to use a different template.</p>}
            {tplPickerOpen && !templateLocked && (
              <>
                {form.template && (
                  <div className="camp-selected-tpl" style={{ marginTop: 10 }}>
                    <code>{form.template.name}</code>
                    <button className="camp-tag-remove" onClick={() => { setForm(f => ({ ...f, template: null, variableMapping: {}, headerMediaUrl: "" })); setVarObjTypes({}); setMediaSize({ loading: false, bytes: null, fetchError: false }); setUrlButtonTargets({}); }}>
                      ×
                    </button>
                  </div>
                )}

                {tplLoaded ? (
                  <>
                    <input
                      className="camp-search-input"
                      placeholder="Search approved templates…"
                      value={tplSearch}
                      onChange={e => setTplSearch(e.target.value)}
                      style={{ marginTop: 10 }}
                    />
                    <div className="camp-list-picker">
                      {filteredTpls.map(t => {
                        const body = t.components?.find(c => c.type === "BODY");
                        const sel  = form.template?.name === t.name;
                        return (
                          <div key={t.id || t.name} className={`camp-list-item ${sel ? "selected" : ""}`}
                            onClick={() => { setForm(f => ({ ...f, template: t, variableMapping: {}, headerMediaUrl: "" })); setVarObjTypes({}); setMediaSize({ loading: false, bytes: null, fetchError: false }); setUrlButtonTargets({}); setTplPickerOpen(false); }}>
                            <div style={{ flex: 1 }}>
                              <span className="camp-list-name">{t.name}</span>
                              {body?.text && (
                                <span className="camp-list-meta">
                                  {body.text.length > 80 ? body.text.slice(0, 80) + "…" : body.text}
                                </span>
                              )}
                            </div>
                            {sel && <span style={{ color: "var(--green-primary)", fontSize: 16 }}>✓</span>}
                          </div>
                        );
                      })}
                      {filteredTpls.length === 0 && (
                        <div style={{ padding: 12, color: "var(--text-muted)", fontSize: 13 }}>No approved templates found.</div>
                      )}
                    </div>
                  </>
                ) : (
                  !tplLoading && (
                    <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 10 }}>Click "Load Templates" to browse approved templates.</p>
                  )
                )}
              </>
            )}

            {!tplPickerOpen && !form.template && (
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 8 }}>Click to expand and select a template.</p>
            )}
          </div>

          {/* Media header upload — shown when the template has a VIDEO/IMAGE/DOCUMENT header */}
          {form.template && hdrMediaFormat && (
            <div className="ct-section">
              <h3 className="ct-section-title">
                {hdrMediaFormat === "VIDEO" ? "Video" : hdrMediaFormat === "IMAGE" ? "Image" : "Document"} Header <span className="ct-req">*</span>
              </h3>
              <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 12 }}>
                This template requires a {hdrMediaFormat.toLowerCase()} header. Provide a publicly accessible URL.
              </p>
              <div className="input-group">
                <label>
                  {hdrMediaFormat === "VIDEO" ? "Video URL" : hdrMediaFormat === "IMAGE" ? "Image URL" : "Document URL"}
                  <span className="ct-req"> *</span>
                </label>
                <input
                  placeholder={
                    hdrMediaFormat === "VIDEO"    ? "https://example.com/video.mp4"
                    : hdrMediaFormat === "IMAGE"  ? "https://example.com/image.jpg"
                    :                               "https://example.com/document.pdf"
                  }
                  value={form.headerMediaUrl}
                  onChange={e => setForm(f => ({ ...f, headerMediaUrl: e.target.value }))}
                />
                {/* Size indicator */}
                {form.headerMediaUrl.trim() && (() => {
                  const limitMB = { VIDEO: 16, IMAGE: 5, DOCUMENT: 100 }[hdrMediaFormat] || 16;
                  if (mediaSize.loading) return (
                    <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>Checking file size…</p>
                  );
                  if (mediaSize.fetchError) return (
                    <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>Could not determine file size — make sure the URL is publicly accessible.</p>
                  );
                  if (mediaSize.bytes !== null) {
                    const sizeMB = mediaSize.bytes / (1024 * 1024);
                    const exceeded = sizeMB > limitMB;
                    return (
                      <p style={{ fontSize: 12, marginTop: 6, color: exceeded ? "var(--red, #ef4444)" : "var(--green-primary, #22c55e)", fontWeight: 500 }}>
                        {exceeded
                          ? `⚠ File size is ${sizeMB.toFixed(2)} MB — exceeds the ${limitMB} MB WhatsApp limit`
                          : `✓ File size: ${sizeMB.toFixed(2)} MB (within ${limitMB} MB limit)`}
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {/* Variable mapping — API campaigns: show variable reference, HubSpot campaigns: show mapper */}
          {form.template && templateVars.length > 0 && form.campaignType === "api" && (
            <div className="ct-section">
              <h3 className="ct-section-title">Template Variables</h3>
              <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 14 }}>
                Pass values for each variable in the API call body under <code style={{ color: "#8b5cf6" }}>variables</code>.
              </p>
              <div style={{ background: "var(--bg-sidebar)", borderRadius: 8, padding: "12px 14px", border: "1px solid var(--border-color)" }}>
                {templateVars.map(v => (
                  <div key={v} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, fontSize: 13 }}>
                    <code style={{ color: "#8b5cf6", minWidth: 50 }}>{`"${v}"`}</code>
                    <span style={{ color: "var(--text-muted)" }}>→ value for <strong>{`{{${v}}}`}</strong> in the template</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {form.template && templateVars.length > 0 && form.campaignType !== "api" && (
            <div className="ct-section">
              <h3 className="ct-section-title">Variable Mapping</h3>
              <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 14 }}>
                For each template variable, select the HubSpot object and property to use, or enter static text.
              </p>
              {templateVars.map(v => {
                const raw = form.variableMapping[v] || "";

                // Parse property from the stored mapping value (used only for the property dropdown)
                let storedProp = "", staticValue = raw;
                if (raw.startsWith("@contact."))  { storedProp = raw.slice(9);  staticValue = ""; }
                else if (raw.startsWith("@deal."))    { storedProp = raw.slice(6);  staticValue = ""; }
                else if (raw.startsWith("@company.")) { storedProp = raw.slice(9);  staticValue = ""; }
                // Legacy compat
                else if (raw === "@firstName") { storedProp = "firstname"; staticValue = ""; }
                else if (raw === "@lastName")  { storedProp = "lastname";  staticValue = ""; }
                else if (raw === "@fullName")  { storedProp = "fullname";  staticValue = ""; }
                else if (raw === "@phone")     { storedProp = "phone";     staticValue = ""; }

                // Object type comes from dedicated UI state — NOT derived from the mapping value.
                // This is the fix: selecting "Contact" stores the choice here even before a property
                // is picked, so the dropdown doesn't snap back to "Static Text".
                const displayObjType = varObjTypes[v] || "static";
                const props = hsProps[displayObjType] || [];

                const handleObjTypeChange = (newObjType) => {
                  // Persist the selection in dedicated state
                  setVarObjTypes(s => ({ ...s, [v]: newObjType }));
                  // Start loading properties for this object type immediately
                  if (newObjType !== "static") loadHsProps(newObjType);
                  // Clear the mapping value when switching object types
                  setForm(f => ({ ...f, variableMapping: { ...f.variableMapping, [v]: "" } }));
                };

                const handlePropertyChange = (prop) => {
                  const val = prop ? `@${displayObjType}.${prop}` : "";
                  setForm(f => ({ ...f, variableMapping: { ...f.variableMapping, [v]: val } }));
                };

                const handleStaticChange = (text) => {
                  setForm(f => ({ ...f, variableMapping: { ...f.variableMapping, [v]: text } }));
                };

                return (
                  <div key={v} style={{ marginBottom: 16, padding: "12px 14px", background: "var(--bg-sidebar)", borderRadius: 8, border: "1px solid var(--border-color)" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 8 }}>
                      Variable {`{{${v}}}`}
                    </label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>

                      {/* Step 1 — Object type */}
                      <select
                        style={{ flex: "0 0 130px", fontSize: 13 }}
                        value={displayObjType}
                        onChange={e => handleObjTypeChange(e.target.value)}
                      >
                        <option value="static">Static Text</option>
                        <option value="contact">Contact</option>
                        <option value="deal">Deal</option>
                        <option value="company">Company</option>
                      </select>

                      {/* Step 2a — Property dropdown */}
                      {displayObjType !== "static" && (
                        <select
                          style={{ flex: 1, minWidth: 180, fontSize: 13 }}
                          value={storedProp}
                          onChange={e => handlePropertyChange(e.target.value)}
                        >
                          <option value="">
                            {hsPropLoading[displayObjType] ? "Loading…" : "— Select property —"}
                          </option>
                          {props.map(p => (
                            <option key={p.name} value={p.name}>{p.label}</option>
                          ))}
                        </select>
                      )}

                      {/* Step 2b — Static text input */}
                      {displayObjType === "static" && (
                        <input
                          style={{ flex: 1, minWidth: 160, fontSize: 13, padding: "6px 10px", background: "var(--bg-main)", border: "1px solid var(--border-color)", borderRadius: 6, color: "var(--text-primary)" }}
                          placeholder={`Static value for {{${v}}}`}
                          value={staticValue}
                          onChange={e => handleStaticChange(e.target.value)}
                        />
                      )}
                    </div>

                    {/* Preview line */}
                    {displayObjType !== "static" && storedProp && (
                      <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                        Will use <code style={{ color: "#8b5cf6" }}>{storedProp}</code> from HubSpot {displayObjType}
                      </p>
                    )}
                    {displayObjType !== "static" && !storedProp && (
                      <p style={{ fontSize: 11, color: "var(--orange)", marginTop: 6 }}>
                        Select a property to complete this mapping
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Link Tracking — only shown when template has a URL button with {{1}} */}
          {urlTrackingButtons.length > 0 && (
            <div className="ct-section">
              <h3 className="ct-section-title">Link Tracking</h3>
              <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 14 }}>
                This template has a URL button with a dynamic suffix. Enter the destination URL — each contact will get a unique tracking link so you can see exactly who clicked.
              </p>
              {urlTrackingButtons.map(btn => (
                <div key={btn.buttonIndex} className="input-group" style={{ marginBottom: 10 }}>
                  <label>Destination URL for button <strong>"{btn.text}"</strong></label>
                  <input
                    placeholder="https://your-destination.com/page"
                    value={urlButtonTargets[btn.buttonIndex] || ""}
                    onChange={e => setUrlButtonTargets(t => ({ ...t, [btn.buttonIndex]: e.target.value }))}
                  />
                  <span style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, display: "block" }}>
                    Template base URL: <code style={{ color: "#8b5cf6" }}>{btn.url?.replace("{{1}}", "")}</code> + unique token per contact
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Batch Processing — hidden for API campaigns */}
          {form.campaignType !== "api" && <div className="ct-section">
            <h3 className="ct-section-title">Batch Processing</h3>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>
              Split large campaigns into batches to stay within Meta's daily messaging limits (Tier 1 = 1,000/day, Tier 2 = 10,000/day).
            </p>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={form.batchEnabled}
                onChange={e => setForm(f => ({ ...f, batchEnabled: e.target.checked }))}
              />
              <span style={{ fontSize: 14, fontWeight: 500 }}>Enable Batch Processing</span>
            </label>
            {form.batchEnabled && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="input-group sm">
                  <label>Contacts per Batch</label>
                  <input
                    type="number" min="1" max="10000"
                    value={form.batchSize}
                    onChange={e => setForm(f => ({ ...f, batchSize: e.target.value }))}
                    placeholder="e.g. 1000"
                  />
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    Set to your Meta tier limit (1,000 or 10,000)
                  </span>
                </div>
                <div className="input-group sm">
                  <label>Delay Between Batches</label>
                  <select
                    value={form.batchDelayMinutes}
                    onChange={e => setForm(f => ({ ...f, batchDelayMinutes: e.target.value }))}
                  >
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="360">6 hours</option>
                    <option value="720">12 hours</option>
                    <option value="1440">24 hours</option>
                  </select>
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    Wait time before sending next batch
                  </span>
                </div>
              </div>
            )}
          </div>}

          {/* Schedule — editable until sending starts */}
          {(!isEditing || canReschedule) && form.campaignType !== "api" && <div className="ct-section">
            <h3 className="ct-section-title">Schedule</h3>
            <div className="camp-schedule-row">
              <label className={`camp-schedule-opt ${form.scheduleType === "now" ? "active" : ""}`}>
                <input type="radio" name="sched" checked={form.scheduleType === "now"}
                  onChange={() => setForm(f => ({ ...f, scheduleType: "now" }))} />
                Send Immediately
              </label>
              <label className={`camp-schedule-opt ${form.scheduleType === "schedule" ? "active" : ""}`}>
                <input type="radio" name="sched" checked={form.scheduleType === "schedule"}
                  onChange={() => setForm(f => ({ ...f, scheduleType: "schedule" }))} />
                Schedule for Later
              </label>
            </div>
            {form.scheduleType === "schedule" && (
              <div className="input-group sm" style={{ marginTop: 10 }}>
                <label>Send At</label>
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            )}
          </div>}

          <button
            className="btn-primary btn-full"
            onClick={handleSubmit}
            disabled={submitting || !form.name.trim() || (form.campaignType !== "api" && !form.lists.length) || !form.template}
          >
            {submitting
              ? (isEditing ? "Saving campaign…" : "Creating campaign…")
              : isEditing ? "Save Changes" : form.campaignType === "api"
                ? "Create API Campaign"
                : form.scheduleType === "now" ? "Send Campaign Now" : "Schedule Campaign"}
          </button>
        </div>

        {/* Right: summary / preview */}
        <div className="camp-create-preview">
          <p className="camp-preview-title">Campaign Summary</p>

          <div className="camp-summary-item">
            <span className="camp-summary-label">Name</span>
            <span className="camp-summary-value">{form.name || "—"}</span>
          </div>
          <div className="camp-summary-item">
            <span className="camp-summary-label">Category</span>
            <span className="camp-summary-value">{form.category || "Not set"}</span>
          </div>
          <div className="camp-summary-item">
            <span className="camp-summary-label">Type</span>
            <span className="camp-summary-value" style={{ color: form.campaignType === "api" ? "#8b5cf6" : undefined }}>
              {form.campaignType === "api" ? "API Triggered" : "HubSpot List"}
            </span>
          </div>
          {form.campaignType !== "api" && (
          <div className="camp-summary-item">
            <span className="camp-summary-label">Lists</span>
            <span className="camp-summary-value">
              {form.lists.length ? form.lists.map(l => l.listName).join(", ") : "—"}
            </span>
          </div>
          )}
          <div className="camp-summary-item">
            <span className="camp-summary-label">Template</span>
            <span className="camp-summary-value">{form.template?.name || "—"}</span>
          </div>
          {preview && (
            <>
              <div className="camp-summary-item">
                <span className="camp-summary-label">Total contacts</span>
                <span className="camp-summary-value">{preview.total}</span>
              </div>
              <div className="camp-summary-item">
                <span className="camp-summary-label">Will receive</span>
                <span className="camp-summary-value" style={{ color: "var(--green-primary)" }}>{preview.validCount}</span>
              </div>
              <div className="camp-summary-item">
                <span className="camp-summary-label">Skipped (no phone)</span>
                <span className="camp-summary-value" style={{ color: "var(--danger)" }}>{preview.invalidCount}</span>
              </div>
            </>
          )}
          {form.scheduleType === "schedule" && form.scheduledAt && (
            <div className="camp-summary-item">
              <span className="camp-summary-label">Scheduled for</span>
              <span className="camp-summary-value">{new Date(form.scheduledAt).toLocaleString()}</span>
            </div>
          )}

          {bodyComp && (
            <div style={{ marginTop: 20 }}>
              <p className="camp-preview-title">Template Preview</p>
              <div className="tpl-phone" style={{ marginTop: 8 }}>
                <div className="tpl-bubble">
                  {hdrComp?.format === "TEXT" && hdrComp.text && (
                    <p className="tpl-header-text">{hdrComp.text}</p>
                  )}
                  <p className="tpl-body-text">{bodyComp.text}</p>
                  {ftrComp?.text && <p className="tpl-footer-text">{ftrComp.text}</p>}
                  <span className="ct-preview-time">Template</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Reusable expandable contact-status panel (Delivered / Read) ──
function ContactStatusPanel({ label, count, color, badgeColor, cta, contacts, loading, emptyMsg, timestampKey, timestampLabel, onOpen, onRefresh, pagination, onPage, onLimit, error }) {
  return (
    <div style={{ margin: "0 0 16px", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "var(--bg-sidebar)", cursor: "pointer" }}
        onClick={onOpen}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color }}>{label}</span>
          <span style={{ fontSize: 12, background: badgeColor, color, padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
            {count} contacts
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {contacts && (
            <button className="btn-sm" onClick={onRefresh} disabled={loading}>
              <Icons.Refresh />
            </button>
          )}
          <span style={{ color: "var(--text-muted)", fontSize: 13 }}>{contacts ? "▲ Hide" : cta}</span>
        </div>
      </div>
      {contacts && (
        <div>
          {error && <p role="alert" className="error-msg">{error}</p>}
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>
          ) : contacts.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>{emptyMsg}</div>
          ) : (
            <table className="tpl-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Contact ID</th>
                  <th>Sent At</th>
                  <th>{timestampLabel}</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c, i) => (
                  <tr key={i} className="tpl-row" style={{ cursor: "default" }}>
                    <td style={{ fontWeight: 500 }}>{c.contactName || "—"}</td>
                    <td><code style={{ fontSize: 12 }}>{c.phone}</code></td>
                    <td style={{ color: "var(--text-secondary)", fontSize: 12 }}>{c.contactId || "—"}</td>
                    <td style={{ color: "var(--text-secondary)", fontSize: 12, whiteSpace: "nowrap" }}>
                      {c.sentAt ? new Date(c.sentAt).toLocaleString() : "—"}
                    </td>
                    <td style={{ color, fontSize: 12, whiteSpace: "nowrap", fontWeight: 500 }}>
                      {c[timestampKey] ? new Date(c[timestampKey]).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination label={label} {...pagination} loading={loading} onPage={onPage} onLimit={onLimit} />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CAMPAIGN DETAIL
// ═══════════════════════════════════════════════════════════════
function CampaignContent({ campaign }) {
  const [definition, setDefinition] = useState(null);
  const [templateError, setTemplateError] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setDefinition(null);
    setTemplateError(false);
    apiFetch("/templates").then(data => {
      if (cancelled) return;
      const template = (data.templates || []).find(t => t.name === campaign.template?.name && t.language === (campaign.template?.language || "en"));
      setDefinition(template || null);
      setTemplateError(!template);
    }).catch(() => { if (!cancelled) setTemplateError(true); });
    return () => { cancelled = true; };
  }, [campaign.template?.name, campaign.template?.language]);
  useEffect(() => { setMediaError(false); }, [campaign.headerMediaUrl]);

  const safeUrl = value => {
    try { const url = new URL(value); return ["http:", "https:"].includes(url.protocol) ? url.href : null; }
    catch { return null; }
  };
  const mediaUrl = safeUrl(campaign.headerMediaUrl);
  const header = definition?.components?.find(c => c.type === "HEADER");
  const body = definition?.components?.find(c => c.type === "BODY")?.text;
  const buttons = definition?.components?.find(c => c.type === "BUTTONS")?.buttons || [];
  const extension = mediaUrl ? new URL(mediaUrl).pathname.split(".").pop().toLowerCase() : "";
  const mediaType = header?.format || campaign.headerMediaType ||
    (["mp4", "webm", "mov", "m4v", "3gp"].includes(extension) ? "VIDEO" : ["jpg", "jpeg", "png", "gif", "webp"].includes(extension) ? "IMAGE" : "DOCUMENT");
  const links = (campaign.urlButtonConfigs || []).map(config => ({
    key: `configured-${config.index}`, label: buttons[config.index]?.text || `Button ${Number(config.index) + 1}`,
    value: config.value, mode: config.mode,
  }));
  if (campaign.trackingTargetUrl && !links.some(link => link.value === campaign.trackingTargetUrl)) {
    links.push({ key: "legacy", label: "Tracking destination", value: campaign.trackingTargetUrl, mode: "tracking" });
  }
  buttons.forEach((button, index) => {
    if (button.type === "URL" && !button.url?.includes("{{")) links.push({ key: `fixed-${index}`, label: button.text || `Button ${index + 1}`, value: button.url, mode: "fixed" });
  });

  return <section className="campaign-content ct-section" aria-label="Campaign content">
    <h3 className="ct-section-title">Campaign content</h3>
    <div className="campaign-content-grid">
      <div>
        <h4>Media</h4>
        {mediaUrl ? <>
          {!mediaError && mediaType === "VIDEO" && <video className="campaign-media" controls preload="metadata" src={mediaUrl} onError={() => setMediaError(true)} />}
          {!mediaError && mediaType === "IMAGE" && <img className="campaign-media" src={mediaUrl} alt={`${campaign.name} campaign attachment`} loading="lazy" onError={() => setMediaError(true)} />}
          {mediaError && <p>Preview unavailable. You can open the attached media below.</p>}
          <a className="campaign-content-link" href={mediaUrl} target="_blank" rel="noopener noreferrer">Open {mediaType === "VIDEO" ? "video" : mediaType === "IMAGE" ? "image" : "attachment"}</a>
          <div className="campaign-content-url">{campaign.headerMediaUrl}</div>
        </> : <p className="wap-subtitle">{campaign.headerMediaUrl ? "The saved media URL is invalid." : "No media attached."}</p>}
      </div>
      <div>
        {body && <><h4>Message template</h4><p className="campaign-content-body">{body}</p></>}
        <h4>Links</h4>
        {links.length ? <ul className="campaign-content-links">{links.map(link => {
          const url = link.mode === "property" ? null : safeUrl(link.value);
          return <li key={link.key}><strong>{link.label}</strong>
            {url ? <a className="campaign-content-link" href={url} target="_blank" rel="noopener noreferrer">{link.value}</a> : <span className="campaign-content-url">{link.value}</span>}
            <small>{link.mode === "tracking" ? "Tracked destination" : link.mode === "property" ? "Personalized from a contact property" : "Template link"}</small>
          </li>;
        })}</ul> : <p className="wap-subtitle">{!definition && !templateError ? "Loading template links…" : "No configured links."}</p>}
        {templateError && <p className="wap-subtitle">Template preview is unavailable. Saved campaign media and destinations are shown above.</p>}
      </div>
    </div>
  </section>;
}

function CampaignDetail({ campaignId, onBack, onFlash, onEdit, onArchive, actionBusy, notice }) {
  const [detail, setDetail]               = useState(null);
  const [categoryDraft, setCategoryDraft] = useState(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryResult, setCategoryResult] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [statusFilter, setStatusFilter]   = useState("all");
  const [msgSearch, setMsgSearch]         = useState("");
  const [page, setPage]                   = useState(1);
  const [pagination, setPagination]       = useState({ total: 0, totalPages: 1 });
  const [retryModal, setRetryModal]       = useState(null);
  const [retrying, setRetrying]           = useState(false);
  const [testModal, setTestModal]         = useState(false);
  const [testPhone, setTestPhone]         = useState("");
  const [testSending, setTestSending]     = useState(false);
  const [testResult, setTestResult]       = useState(null);
  const clickedData = useCampaignData(campaignId, "clicked", "clicked");
  const deliveredData = useCampaignData(campaignId, "contacts?status=delivered", "contacts");
  const readData = useCampaignData(campaignId, "contacts?status=read", "contacts");
  const { items: clickedContacts, loading: clickedLoading, load: fetchClickedContacts } = clickedData;
  const { items: deliveredContacts, loading: deliveredLoading, load: fetchDeliveredContacts } = deliveredData;
  const { items: readContacts, loading: readLoading, load: fetchReadContacts } = readData;
  const [curlCopied, setCurlCopied]               = useState(false);
  const searchTimerRef = useRef(null);

  const [pageSize, setPageSize] = useState(25);
  const detailRequest = useRef(0);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const fetchDetail = async (p = page, sf = statusFilter, sq = msgSearch, limit = pageSize) => {
    const request = ++detailRequest.current;
    setMessagesLoading(true);
    const params = new URLSearchParams({ page: p, limit, status: sf });
    if (sq) params.set("search", sq);
    try {
      const data = await apiFetch(`/campaigns/${campaignId}?${params}`);
      if (request !== detailRequest.current) return;
      if (data.error) throw new Error(data.error);
      setDetail(data);
      setPage(data.page || 1);
      setPagination({ total: data.total || 0, totalPages: data.totalPages || 1 });
      setLoading(false);
    } catch (error) { if (request === detailRequest.current) onFlash(error.message || "Failed to load messages", false); }
    finally { if (request === detailRequest.current) setMessagesLoading(false); }
  };

  useEffect(() => { setCategoryDraft(null); setCategoryResult(null); fetchDetail(1, "all", ""); }, [campaignId]);

  const saveCategory = async () => {
    setCategorySaving(true);
    setCategoryResult(null);
    try {
      const data = await apiFetch(`/campaigns/${campaignId}`, {
        method: "PATCH", body: JSON.stringify({ category: categoryDraft }),
      });
      if (!data.success) throw new Error(data.error || "Failed to save category");
      setDetail(previous => ({ ...previous, campaign: { ...previous.campaign, category: data.campaign.category } }));
      setCategoryDraft(null);
      setCategoryResult({ ok: true, text: "Category saved" });
    } catch (error) {
      setCategoryResult({ ok: false, text: error.message || "Failed to save category" });
    } finally {
      setCategorySaving(false);
    }
  };

  const handleStatusFilter = (s) => {
    clearTimeout(searchTimerRef.current);
    setStatusFilter(s);
    setPage(1);
    fetchDetail(1, s, msgSearch);
  };

  const handleSearch = (val) => {
    setMsgSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      fetchDetail(1, statusFilter, val);
    }, 350);
  };

  const handlePage = (p) => {
    clearTimeout(searchTimerRef.current);
    setPage(p);
    fetchDetail(p, statusFilter, msgSearch);
  };

  useEffect(() => {
    const status = detail?.campaign?.status;
    if (!status || status === "pending" || status === "scheduled" || status === "failed") return;
    // Fast poll while sending; medium poll when batch_paused; slow poll when fully sent
    const interval = status === "sending" ? 5000 : status === "batch_paused" ? 15000 : 30000;
    const t = setInterval(() => fetchDetail(page, statusFilter, msgSearch), interval);
    return () => clearInterval(t);
  }, [detail?.campaign?.status, page, statusFilter, msgSearch, pageSize]);

  const handleRetry = async () => {
    setRetrying(true);
    const data = await apiFetch(`/campaigns/${campaignId}/retry`, {
      method: "POST",
      body: JSON.stringify({
        filter:      retryModal.filter,
        scheduledAt: retryModal.scheduleType === "schedule" && retryModal.scheduledAt
          ? new Date(retryModal.scheduledAt).toISOString()
          : null,
      }),
    });
    setRetrying(false);
    setRetryModal(null);
    if (data.success) {
      onFlash(`Retry queued for ${data.scheduled} message${data.scheduled !== 1 ? "s" : ""}`);
      fetchDetail();
    } else {
      onFlash(data.error || "Retry failed", false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone.trim()) return;
    setTestSending(true);
    setTestResult(null);
    const data = await apiFetch(`/campaigns/${campaignId}/test`, {
      method: "POST",
      body: JSON.stringify({ phone: testPhone.trim() }),
    });
    setTestSending(false);
    setTestResult(data.success ? { success: true, waId: data.waId } : { success: false, error: data.error || "Failed to send" });
  };

  if (loading) return <div className="template-loading">Loading campaign…</div>;
  if (!detail?.campaign) return <div className="template-loading">Campaign not found.</div>;

  const { campaign, messages = [] } = detail;
  const stats = campaign.stats || {};

  const STATUS_COLORS = {
    pending:     "var(--text-muted)",
    queued:      "var(--text-secondary)",
    sent:        "var(--brand-sky)",
    delivered:   "var(--green-primary)",
    read:        "#53bdeb",
    failed:      "var(--danger)",
    undelivered: "var(--orange)",
  };

  // Filtering and counts come from the server — messages is already the current page slice
  const filtered = messages;

  const retryEligible       = { length: stats.retryEligible       || 0 };
  const failedEligible      = { length: stats.failedEligible      || 0 };
  const undeliveredEligible = { length: stats.undeliveredEligible || 0 };
  const sentEligible        = { length: stats.sentEligible        || 0 };

  const STAT_CARDS = [
    { label: "Total",        value: stats.total        || 0, color: ""                         },
    { label: "Sent",         value: stats.sent         || 0, color: STATUS_COLORS.sent         },
    { label: "Delivered",    value: (stats.delivered || 0) + (stats.read || 0), color: STATUS_COLORS.delivered },
    { label: "Read",         value: stats.read         || 0, color: STATUS_COLORS.read         },
    { label: "Replied",      value: stats.replied      || 0, color: "#10b981"                  },
    { label: "Link Clicks",  value: stats.linkClicks   || 0, color: "#8b5cf6"                  },
    { label: "Failed",       value: stats.failed       || 0, color: STATUS_COLORS.failed       },
    { label: "Pending",      value: stats.pending      || 0, color: STATUS_COLORS.pending      },
    { label: "Delivery %",   value: `${stats.deliveryRate || 0}%`, color: "var(--green-primary)" },
    { label: "Read Rate %",  value: `${stats.readRate   || 0}%`, color: STATUS_COLORS.read     },
    { label: "Reply Rate %", value: `${stats.replyRate  || 0}%`, color: "#10b981"              },
    { label: "Click Rate %", value: `${stats.clickRate  || 0}%`, color: "#8b5cf6"              },
  ];

  const STATUS_BADGE_MAP = {
    pending:      "camp-s-pending",
    scheduled:    "camp-s-scheduled",
    sending:      "camp-s-sending",
    batch_paused: "camp-s-scheduled",
    sent:         "camp-s-sent",
    failed:       "camp-s-failed",
    api_active:   "camp-s-api",
  };

  const copyCurl = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCurlCopied(true); setTimeout(() => setCurlCopied(false), 2000); });
  };

  const curlVars    = campaign.type === "api" ? (campaign.apiVars || []) : [];
  const curlBody    = campaign.type === "api" ? {
    to: "919876543210",
    contactName: "John Doe",
    contactId: "optional-hubspot-id",
    ...(curlVars.length ? { variables: Object.fromEntries(curlVars.map(v => [v, "value" + v])) } : {}),
  } : null;
  const curlCommand = campaign.type === "api"
    ? `curl -X POST ${window.location.origin}/api/v1/campaigns/${campaign.id}/send \\\n  -H "Content-Type: application/json" \\\n  -H "X-API-Key: mfe_live_YOUR_API_KEY" \\\n  -d '${JSON.stringify(curlBody, null, 2).replace(/'/g, "'\\''")}'`
    : "";

  return (
    <div className="camp-detail-root">
      {/* Header */}
      <div className="camp-detail-header">
        <button className="btn-icon" onClick={onBack}><Icons.ArrowLeft /></button>
        <div style={{ flex: 1 }}>
          <h2>{campaign.name}</h2>
          <div className="camp-detail-meta">
            <span>Template: <strong>{campaign.template?.name}</strong></span>
            {campaign.type === "api"
              ? <span style={{ color: "#8b5cf6", fontWeight: 600 }}>API Triggered</span>
              : <span>Lists: <strong>{(campaign.hubspotLists || []).map(l => l.listName).join(", ")}</strong></span>
            }
            <span>By: <strong>{campaign.createdBy}</strong></span>
            <span>{new Date(campaign.createdAt).toLocaleString()}</span>
            {campaign.scheduledAt && <span>Scheduled: <strong>{new Date(campaign.scheduledAt).toLocaleString()}</strong></span>}
            {campaign.batchSize > 0 && (
              <span>Batch size: <strong>{campaign.batchSize.toLocaleString()} contacts</strong></span>
            )}
          </div>
        </div>
        <span className={`camp-status-badge ${STATUS_BADGE_MAP[campaign.status] || ""}`}>
          {campaign.status === "batch_paused" ? "batch paused" : campaign.status}
        </span>
        <button className="btn-sm" onClick={() => { setTestResult(null); setTestPhone(""); setTestModal(true); }}
          title="Send a test message to yourself">
          🧪 Test
        </button>
        {can(getUser(), "send_campaign") && <>
          <button className="btn-sm" disabled={actionBusy || campaign.status === "sending"} onClick={onEdit}>Edit</button>
          <button className="btn-sm" style={{ color: "var(--danger)" }} disabled={actionBusy} onClick={() => onArchive(campaign)}>Archive</button>
        </>}
        <button className="btn-sm" onClick={() => fetchDetail()}><Icons.Refresh /></button>
      </div>
      {notice?.text && <div role="status" className={`wap-alert ${notice.ok ? "ok" : "err"}`}>{notice.text}</div>}
      <CampaignContent campaign={campaign} />

      {/* API Integration CURL panel */}
      <div className="ct-section">
        <div className="input-group">
          <label htmlFor="existing-campaign-category">Category</label>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <select id="existing-campaign-category" value={categoryDraft ?? campaign.category ?? ""}
              disabled={categorySaving || !can(getUser(), "send_campaign")}
              onChange={e => { setCategoryDraft(e.target.value); setCategoryResult(null); }}>
              <option value="">Not set</option>
              {CAMPAIGN_CATEGORIES.map(category => <option key={category} value={category}>{category}</option>)}
            </select>
            {can(getUser(), "send_campaign") && <button className="btn-sm" onClick={saveCategory}
              disabled={categorySaving || categoryDraft === null || categoryDraft === (campaign.category || "")}>
              {categorySaving ? "Saving…" : "Save category"}
            </button>}
          </div>
          {categoryResult && <p role="status" style={{ color: categoryResult.ok ? "var(--green-primary)" : "var(--danger)" }}>{categoryResult.text}</p>}
        </div>
      </div>

      {campaign.type === "api" && (
        <div style={{ margin: "0 0 16px", borderRadius: 10, border: "1px solid #334155", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#1e293b" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#8b5cf6" }}>n8n / API Integration</span>
              <span style={{ fontSize: 11, background: "#8b5cf620", color: "#8b5cf6", padding: "2px 8px", borderRadius: 10 }}>
                Campaign ID: {campaign.id}
              </span>
            </div>
            <button className="btn-sm" onClick={() => copyCurl(curlCommand)} style={{ fontSize: 12 }}>
              {curlCopied ? "✓ Copied!" : "Copy CURL"}
            </button>
          </div>
          <div style={{ background: "#0f172a", padding: "14px 16px", overflowX: "auto" }}>
            <pre style={{ margin: 0, fontSize: 12, color: "#94a3b8", whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.7 }}>
              {curlCommand}
            </pre>
          </div>
          <div style={{ background: "#1e293b", padding: "8px 16px", fontSize: 11, color: "#64748b" }}>
            Get your API key from <strong style={{ color: "#94a3b8" }}>Admin → API Keys</strong>.
            Replace <code style={{ color: "#8b5cf6" }}>919876543210</code> with the recipient phone (country code, no +).
            {curlVars.length > 0 && <> Replace each <code style={{ color: "#8b5cf6" }}>value1</code>, <code style={{ color: "#8b5cf6" }}>value2</code> etc. with the actual template variable values.</>}
          </div>
        </div>
      )}

      {/* Batch progress banner */}
      {campaign.batchSize > 0 && campaign.totalBatches > 0 && (
        <div style={{
          margin: "0 0 16px", padding: "12px 16px", borderRadius: 10,
          background: campaign.status === "batch_paused" ? "rgba(245,158,11,.08)" : "rgba(59,130,246,.08)",
          border: `1px solid ${campaign.status === "batch_paused" ? "rgba(245,158,11,.3)" : "rgba(59,130,246,.3)"}`,
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: campaign.status === "batch_paused" ? "#b45309" : "#1d4ed8" }}>
              Batch {campaign.currentBatch} of {campaign.totalBatches}
              {" — "}{campaign.batchSize.toLocaleString()} contacts per batch
            </div>
            {campaign.status === "batch_paused" && campaign.nextBatchAt && (
              <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>
                Next batch starts: <strong>{new Date(campaign.nextBatchAt).toLocaleString()}</strong>
              </div>
            )}
            {campaign.status === "sending" && (
              <div style={{ fontSize: 12, color: "#1e40af", marginTop: 2 }}>
                Sending batch {campaign.currentBatch}… page refreshes automatically.
              </div>
            )}
          </div>
          {/* Progress bar */}
          <div style={{ width: 200, height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              width: `${Math.round((campaign.currentBatch / campaign.totalBatches) * 100)}%`,
              height: "100%",
              background: campaign.status === "batch_paused" ? "#f59e0b" : "#3b82f6",
              borderRadius: 3, transition: "width .4s",
            }} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>
            {Math.round((campaign.currentBatch / campaign.totalBatches) * 100)}%
          </div>
        </div>
      )}

      {/* Test message modal */}
      {testModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, width: 460, padding: 28, boxShadow: "0 20px 50px rgba(0,0,0,.2)" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Send Test Message</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 20 }}>
              Sends the campaign template to your phone. Test messages are excluded from campaign analytics.
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
                Your Phone Number (with country code, no +)
              </label>
              <input
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                placeholder="e.g. 919876543210"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !testSending && handleSendTest()}
                autoFocus
              />
            </div>
            {testResult && (
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: testResult.success ? "#f0fdf4" : "#fef2f2", border: `1px solid ${testResult.success ? "#86efac" : "#fecaca"}`, fontSize: 13, color: testResult.success ? "#166534" : "#dc2626" }}>
                {testResult.success ? `✓ Test message sent! (ID: ${testResult.waId || "—"})` : `✕ ${testResult.error}`}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setTestModal(false); setTestResult(null); }}
                style={{ padding: "8px 16px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", cursor: "pointer", fontSize: 13 }}>
                Close
              </button>
              <button onClick={handleSendTest} disabled={testSending || !testPhone.trim()}
                style={{ padding: "8px 16px", background: testSending ? "#94a3b8" : "#6366f1", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                {testSending ? "Sending…" : "Send Test"}
              </button>
            </div>
          </div>
        </div>
      )}

      {campaign.error && (
        <div className="wap-alert err" style={{ margin: "12px 24px 0" }}>Error: {campaign.error}</div>
      )}

      {/* Stats grid */}
      <div className="camp-stats-grid">
        {STAT_CARDS.map(s => (
          <div key={s.label} className="camp-stat-card">
            <span className="camp-stat-card-num" style={{ color: s.color || "var(--text-primary)" }}>
              {s.value}
            </span>
            <span className="camp-stat-card-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Delivered Contacts panel */}
      {((stats.delivered || 0) + (stats.read || 0)) > 0 && (
        <ContactStatusPanel
          label="Delivered Contacts"
          count={(stats.delivered || 0) + (stats.read || 0)}
          color="#22c55e"
          badgeColor="#22c55e20"
          cta="▼ View who received it"
          contacts={deliveredContacts}
          loading={deliveredLoading}
          pagination={deliveredData.pagination} error={deliveredData.error}
          onPage={p => deliveredData.load(p)} onLimit={limit => deliveredData.load(1, limit)}
          emptyMsg="No deliveries recorded yet."
          timestampKey="deliveredAt"
          timestampLabel="Delivered At"
          onOpen={() => { if (!deliveredContacts) fetchDeliveredContacts(); else deliveredData.close(); }}
          onRefresh={e => { e.stopPropagation(); fetchDeliveredContacts(); }}
        />
      )}

      {/* Read Contacts panel */}
      {(stats.read || 0) > 0 && (
        <ContactStatusPanel
          label="Read Contacts"
          count={stats.read || 0}
          color="#53bdeb"
          badgeColor="#53bdeb20"
          cta="▼ View who read it"
          contacts={readContacts}
          loading={readLoading}
          pagination={readData.pagination} error={readData.error}
          onPage={p => readData.load(p)} onLimit={limit => readData.load(1, limit)}
          emptyMsg="No reads recorded yet."
          timestampKey="readAt"
          timestampLabel="Read At"
          onOpen={() => { if (!readContacts) fetchReadContacts(); else readData.close(); }}
          onRefresh={e => { e.stopPropagation(); fetchReadContacts(); }}
        />
      )}

      {/* Clicked Contacts — shown when campaign has link tracking */}
      {campaign.urlButtonConfigs?.length > 0 && (
        <div style={{ margin: "0 0 16px", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "var(--bg-sidebar)", cursor: "pointer" }}
            onClick={() => { if (!clickedContacts) fetchClickedContacts(); else clickedData.close(); }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#8b5cf6" }}>Clicked Contacts</span>
              <span style={{ fontSize: 12, background: "#8b5cf620", color: "#8b5cf6", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
                {stats.linkClicks || 0} clicks
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {clickedContacts && (
                <button className="btn-sm" onClick={e => { e.stopPropagation(); fetchClickedContacts(); }} disabled={clickedLoading}>
                  <Icons.Refresh />
                </button>
              )}
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>{clickedContacts ? "▲ Hide" : "▼ View who clicked"}</span>
            </div>
          </div>
          {clickedContacts && (
            <div style={{ padding: "0" }}>
              {clickedData.error && <p role="alert" className="error-msg">{clickedData.error}</p>}
              {clickedLoading ? (
                <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>
              ) : clickedContacts.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>No contacts have clicked yet.</div>
              ) : (
                <table className="tpl-table">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Phone</th>
                      <th>Contact ID</th>
                      <th style={{ textAlign: "center" }}>Clicks</th>
                      <th>First Clicked</th>
                      <th>Last Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clickedContacts.map((c, i) => (
                      <tr key={i} className="tpl-row" style={{ cursor: "default" }}>
                        <td style={{ fontWeight: 500 }}>{c.contactName || "—"}</td>
                        <td><code style={{ fontSize: 12 }}>{c.phone}</code></td>
                        <td style={{ color: "var(--text-secondary)", fontSize: 12 }}>{c.contactId || "—"}</td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{ color: "#8b5cf6", fontWeight: 700 }}>{c.clickCount}</span>
                        </td>
                        <td style={{ color: "var(--text-secondary)", fontSize: 12, whiteSpace: "nowrap" }}>
                          {c.firstClickAt ? new Date(c.firstClickAt).toLocaleString() : "—"}
                        </td>
                        <td style={{ color: "var(--text-secondary)", fontSize: 12, whiteSpace: "nowrap" }}>
                          {c.lastClickAt ? new Date(c.lastClickAt).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination label="Clicked contacts" {...clickedData.pagination} loading={clickedLoading}
                onPage={p => clickedData.load(p)} onLimit={limit => clickedData.load(1, limit)} />
            </div>
          )}
        </div>
      )}

      {/* Retry section */}
      {(retryEligible.length > 0 || sentEligible.length > 0) && (
        <div className="camp-retry-section">
          <span className="camp-retry-info">
            🔄{retryEligible.length > 0 && <> <strong>{retryEligible.length}</strong> message{retryEligible.length !== 1 ? "s" : ""} eligible for retry</>}
            {retryEligible.length > 0 && sentEligible.length > 0 && " · "}
            {sentEligible.length > 0 && <><strong>{sentEligible.length}</strong> sent (not yet delivered)</>}
          </span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {failedEligible.length > 0 && (
              <button className="btn-sm" onClick={() => setRetryModal({ filter: "failed", scheduleType: "now", scheduledAt: "" })}>
                Retry Failed ({failedEligible.length})
              </button>
            )}
            {undeliveredEligible.length > 0 && (
              <button className="btn-sm" onClick={() => setRetryModal({ filter: "undelivered", scheduleType: "now", scheduledAt: "" })}>
                Retry Undelivered ({undeliveredEligible.length})
              </button>
            )}
            {retryEligible.length > 0 && (
              <button className="btn-primary" style={{ padding: "5px 14px", fontSize: 12 }}
                onClick={() => setRetryModal({ filter: "all", scheduleType: "now", scheduledAt: "" })}>
                Retry All Eligible ({retryEligible.length})
              </button>
            )}
            {sentEligible.length > 0 && (
              <button className="btn-sm" onClick={() => setRetryModal({ filter: "sent", scheduleType: "now", scheduledAt: "" })}>
                Retry on Sent ({sentEligible.length})
              </button>
            )}
            {sentEligible.length > 0 && (
              <button className="btn-sm" onClick={() => setRetryModal({ filter: "sent", scheduleType: "schedule", scheduledAt: "" })}>
                Schedule Retry on Sent
              </button>
            )}
          </div>
        </div>
      )}

      {/* Retry history summary */}
      {(stats.retryBreakdown || []).some(r => r.total > 0) && (
        <div className="camp-retry-summary">
          {(stats.retryBreakdown || []).map(({ attempt, total, successful }) => {
            if (!total) return null;
            return (
              <div key={attempt} className="camp-retry-row">
                Retry {attempt}: <strong>{total}</strong> retried · <strong style={{ color: "var(--green-primary)" }}>{successful}</strong> delivered
              </div>
            );
          })}
        </div>
      )}

      {/* Messages table */}
      <div className="camp-messages-section">
        <div className="camp-messages-toolbar">
          <div className="camp-status-filters">
            {["all", "sent", "delivered", "read", "failed", "undelivered", "pending"].map(s => (
              <button
                key={s}
                className={`camp-filter-btn ${statusFilter === s ? "active" : ""}`}
                onClick={() => handleStatusFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
                {s !== "all" && (
                  <span className="camp-filter-count">
                    {stats[s] || 0}
                  </span>
                )}
              </button>
            ))}
          </div>
          <input
            className="camp-search-input"
            style={{ width: 200 }}
            placeholder="Search contacts…"
            value={msgSearch}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>

        <div className="camp-table-wrap">
          <table className="tpl-table">
            <thead>
              <tr>
                <th>Contact</th>
                <th>Phone</th>
                <th>{campaign.type === "api" ? "Source" : "List"}</th>
                <th>Status</th>
                <th>Sent At</th>
                <th>Clicks</th>
                <th>Retries</th>
                <th>Failure Reason</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", color: "var(--text-muted)", padding: 24 }}>
                  No messages match your filters.
                </td></tr>
              ) : filtered.map(m => (
                <tr key={m.id} className="tpl-row" style={{ cursor: "default" }}>
                  <td>
                    <span style={{ fontWeight: 500 }}>{m.contactName || "—"}</span>
                    {m.contactId && (
                      <span className="am-agent-username" style={{ display: "block" }}>{m.contactId}</span>
                    )}
                  </td>
                  <td><code style={{ fontSize: 12 }}>{m.phone}</code></td>
                  <td style={{ color: "var(--text-secondary)", fontSize: 12 }}>{m.hubspotListName || "—"}</td>
                  <td>
                    <span style={{ color: STATUS_COLORS[m.status] || "var(--text-muted)", fontWeight: 500, fontSize: 13 }}>
                      ● {m.status}
                    </span>
                  </td>
                  <td style={{ color: "var(--text-secondary)", fontSize: 12, whiteSpace: "nowrap" }}>
                    {m.sentAt ? new Date(m.sentAt).toLocaleString() : "—"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {(m.linkClicks || 0) > 0
                      ? <span style={{ color: "#8b5cf6", fontWeight: 600 }}>{m.linkClicks}</span>
                      : <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {m.retryCount > 0
                      ? <span className="camp-retry-badge">{m.retryCount}/3</span>
                      : <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td style={{ color: "var(--danger)", fontSize: 12, maxWidth: 200 }}>
                    {m.failureReason
                      ? (m.failureReason.length > 60 ? m.failureReason.slice(0, 60) + "…" : m.failureReason)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination label="Campaign messages" page={page} limit={pageSize} {...pagination} loading={messagesLoading}
          onPage={handlePage} onLimit={limit => { clearTimeout(searchTimerRef.current); setPageSize(limit); setPage(1); fetchDetail(1, statusFilter, msgSearch, limit); }} />

      </div>

      {/* Retry modal */}
      {retryModal && (
        <div className="modal-overlay" onClick={() => setRetryModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Retry Messages</h3>
              <button className="btn-icon" onClick={() => setRetryModal(null)}>✕</button>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 16 }}>
                Retry <strong>{retryModal.filter === "all" ? "all failed & undelivered" : retryModal.filter === "sent" ? "sent (not yet delivered)" : retryModal.filter}</strong> messages.
                {retryModal.filter === "sent"
                  ? " These messages were accepted by WhatsApp but delivery is unconfirmed."
                  : " Delivered and read messages are never retried."
                } Max 3 attempts per contact.
              </p>
              <div className="camp-schedule-row" style={{ marginBottom: 12 }}>
                <label className={`camp-schedule-opt ${retryModal.scheduleType === "now" ? "active" : ""}`}>
                  <input type="radio" checked={retryModal.scheduleType === "now"}
                    onChange={() => setRetryModal(m => ({ ...m, scheduleType: "now" }))} />
                  Retry Now
                </label>
                <label className={`camp-schedule-opt ${retryModal.scheduleType === "schedule" ? "active" : ""}`}>
                  <input type="radio" checked={retryModal.scheduleType === "schedule"}
                    onChange={() => setRetryModal(m => ({ ...m, scheduleType: "schedule" }))} />
                  Schedule Retry
                </label>
              </div>
              {retryModal.scheduleType === "schedule" && (
                <div className="input-group sm">
                  <label>Retry At</label>
                  <input type="datetime-local" value={retryModal.scheduledAt}
                    onChange={e => setRetryModal(m => ({ ...m, scheduledAt: e.target.value }))}
                    min={new Date().toISOString().slice(0, 16)} />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleRetry} disabled={retrying}>
                {retrying ? "Scheduling…" : "Confirm Retry"}
              </button>
              <button className="btn-sm" onClick={() => setRetryModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMPLATE PREVIEW MODAL
// ═══════════════════════════════════════════════════════════════
function TemplatePreviewModal({ template, onClose }) {
  if (!template) return null;
  const header = template.components?.find(c => c.type === "HEADER");
  const body   = template.components?.find(c => c.type === "BODY");
  const footer = template.components?.find(c => c.type === "FOOTER");
  const buttons = template.components?.find(c => c.type === "BUTTONS");

  return (
    <div className="tpl-modal-overlay" onClick={onClose}>
      <div className="tpl-modal" onClick={e => e.stopPropagation()}>
        <div className="tpl-modal-header">
          <div>
            <h3>{template.name}</h3>
            <span className="tpl-modal-meta">{template.language} · {template.category}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span className={`template-status ${template.status}`}>{template.status}</span>
            <button className="btn-icon" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="tpl-modal-body">
          <p className="tpl-preview-label">Preview</p>
          <div className="tpl-phone">
            <div className="tpl-bubble">
              {header?.text && <p className="tpl-header-text">{header.text}</p>}
              {header?.format && header.format !== "TEXT" && (
                <div className="tpl-media-placeholder">{header.format}</div>
              )}
              <p className="tpl-body-text">{body?.text || ""}</p>
              {footer?.text && <p className="tpl-footer-text">{footer.text}</p>}
              <span className="ct-preview-time">10:30 AM</span>
            </div>
            {buttons?.buttons?.map((btn, i) => (
              <div key={i} className="ct-preview-btn">
                {btn.type === "URL" ? "🔗 " : btn.type === "PHONE_NUMBER" ? "📞 " : "↩ "}
                {btn.text}
              </div>
            ))}
          </div>
          {body?.text && (
            <div className="tpl-raw-body">
              <p className="tpl-preview-label">Message Body</p>
              <p>{body.text}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FULL TEMPLATES VIEW
// ═══════════════════════════════════════════════════════════════
function TemplatesView({ onCreate, canCreate = false, canDelete = false }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [templatePage, setTemplatePage] = useState(1);
  const [templateLimit, setTemplateLimit] = useState(25);
  const [cursors, setCursors] = useState([""]);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState("");
  const templateRequest = useRef(0);
  const cursor = cursors[templatePage - 1] || "";
  const [deleting, setDeleting] = useState(null); // name being deleted

  const fetchTemplates = useCallback(async () => {
    const request = ++templateRequest.current;
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/templates?limit=${templateLimit}${cursor ? `&after=${encodeURIComponent(cursor)}` : ""}`);
      if (request !== templateRequest.current) return;
      if (data.error) throw new Error(data.error);
      setTemplates(data.templates || []);
      setNextCursor(data.nextCursor || null);
    } catch (error) { if (request === templateRequest.current) setError(error.message || "Failed to load templates"); }
    finally { if (request === templateRequest.current) setLoading(false); }
  }, [cursor, templateLimit]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const refreshTemplates = () => {
    setCursors([""]);
    setTemplatePage(1);
    if (templatePage === 1) fetchTemplates();
  };

  const changeTemplatePage = page => {
    if (page > templatePage) {
      if (!nextCursor) return;
      setCursors(previous => [...previous.slice(0, templatePage), nextCursor]);
    }
    setTemplatePage(page);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    setDeleting(t.id || t.name);
    try {
      const qs = t.id ? `?hsm_id=${encodeURIComponent(t.id)}` : "";
      const data = await apiFetch(`/templates/${encodeURIComponent(t.name)}${qs}`, { method: "DELETE" });
      if (data.success) {
        // Remove by id (handles duplicate names with different languages)
        refreshTemplates();
      } else {
        const msg = data.details?.error?.message || data.error || "Failed to delete template.";
        alert(`Delete failed: ${msg}`);
      }
    } catch (err) {
      alert(`Network error: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const LANG_LABELS = Object.fromEntries(LANGUAGES.map(l => [l.code, l.label]));

  return (
    <div className="templates-view">
      {preview && <TemplatePreviewModal template={preview} onClose={() => setPreview(null)} />}

      <div className="templates-view-header">
        <h2>Message Templates</h2>
        <div style={{ display: "flex", gap: "8px" }}>
          <button className="btn-sm" onClick={refreshTemplates}><Icons.Refresh /> Refresh</button>
          {canCreate && (
            <button className="btn-primary" style={{ padding: "6px 14px", fontSize: "13px" }} onClick={onCreate}>+ Create</button>
          )}
        </div>
      </div>

      {error && <p className="error-msg" role="alert">{error}</p>}
      {loading ? (
        <div className="template-loading">Loading templates from Meta...</div>
      ) : templates.length === 0 ? (
        <div className="template-loading">No templates found. Create your first one!</div>
      ) : (
        <div className="tpl-table-wrap">
          <table className="tpl-table">
            <thead>
              <tr>
                <th>Template Name</th>
                <th>Category</th>
                <th>Language</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t, i) => {
                const body = t.components?.find(c => c.type === "BODY");
                const preview_text = body?.text
                  ? body.text.length > 55 ? body.text.slice(0, 55) + "…" : body.text
                  : null;
                return (
                  <tr key={i} className="tpl-row" onClick={() => setPreview(t)}>
                    <td>
                      <span className="tpl-name">{t.name}</span>
                      {preview_text && <span className="tpl-preview-snippet">{preview_text}</span>}
                    </td>
                    <td>{t.category}</td>
                    <td>{LANG_LABELS[t.language] || t.language}</td>
                    <td>
                      <span className={`tpl-status-badge ${t.status}`}>
                        {t.status === "APPROVED" ? "Active" : t.status === "PENDING" || t.status === "IN_APPEAL" ? "In review" : t.status}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      {canDelete && (
                        <button
                          className="btn-delete-tpl"
                          onClick={() => handleDelete(t)}
                          disabled={deleting === (t.id || t.name)}
                          title="Delete template"
                        >
                          {deleting === (t.id || t.name) ? "..." : "Delete"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pagination label="Templates" page={templatePage} limit={templateLimit} hasNext={!!nextCursor} loading={loading || !!deleting}
        onPage={changeTemplatePage} onLimit={limit => { setTemplateLimit(limit); setTemplatePage(1); setCursors([""]); }} />
    </div>
  );
}
